import FastCheck from 'fast-check';
import _ from 'underscore';

import {ARCHIVE_RECORD, ARCHIVE_RECORD_GQL} from 'archive/__tests__/ArchiveRecord-fixtures';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {
  AuthParentOption,
  RegistryLabelingStrategy,
  SchemaInterfaceExtendingSchema,
  SequenceSchemaType,
  TagSchema,
  TagSchemaContainableType,
  TagSchemaSummary,
} from 'constants/RegistryConstants';
import {BatchSchemaNamingScheme} from 'constants/SampleConstants';
import {Status as ComputedFieldStatus} from 'constants/StatusConstants';
import {
  ComputedSchemaTag,
  CustomTag,
  SchemaFieldDataType,
  SchemaTag,
  TargetFile,
  UnaffiliatedSchemaTag,
} from 'folder-items/FileConstants';
import {
  FieldMigrationStatus,
  NameTemplatePartType,
  SchemaFieldNode,
  TagSchemaAuthParentOption,
  TagSchemaConstraintStatus,
  TagSchemaContainableType as TagSchemaContainableTypeEnum,
  TagSchemaItemType,
  TagSchemaNode,
  TagSchemaSequenceSchemaType,
  TagSchemaSupportedFolderItemType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {restToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {MODEL_FIELD_DISPLAY_NAME, MODEL_FIELD_SYSTEM_NAME} from 'instruments/equipment/EquipmentConstants';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {REQUIRED_FIELD_ERROR_LABEL} from 'metadata/SchemaFieldValueError';
import {
  BatchLinkSchemaFieldTypedValue,
  BioentityLinkSchemaFieldTypedValue,
  EntryLinkSchemaFieldTypedValue,
  StorableLinkSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {StorableSchemaSummary} from 'samples/storables/StorableSchemaConstants';
import {EXPIRATION_DATE_FIELD_DISPLAY_NAME} from 'samples/valid-contents/ValidContentsConstants';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {
  AssayLinkSchemaField,
  BioentityLinkFieldWithSchemaInterface,
  BioentityLinkFieldWithTagSchema,
  ComputedSchemaField,
  FieldType,
  RequiredLinkStorableSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import {BenchlingBlob, BlobUploadStatus, WarehouseBlob} from 'schemas/tags/BlobConstants';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';
import {
  ItemWithValidationErrorLabelSummary,
  ItemWithValidationStatusSummary,
  ValidationErrorLabelSummary,
  ValidationStatusSummary,
} from 'settings/registry/validation/ValidationSummaries';
import {apiIdentifierArbitrary} from 'test/fixtures/Arbitraries';
import {ASSAY_SCHEMAS_BY_ID} from 'test/fixtures/AssaySchemaFixtures';
import {TUBE_CONTAINER_SCHEMA_SUMMARY} from 'test/fixtures/inventory/ContainerSchemaFixtures';
import {FULL_PERMISSIONS_GRAPHQL, SCHEMA_PERMISSIONS_GQL} from 'test/fixtures/PermissionFixtures';
import {REGISTRY, REGISTRY_NODE, REGISTRY_SUMMARY} from 'test/fixtures/registry/RegistryFixtures';
import {LITERAL_COMPUTED_FIELD_DEPENDENCY} from 'test/fixtures/schemas/SchemaFieldFixtures';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import pick from 'util/pick';
import pickDefined from 'util/pickDefined';
import {MakeFieldsRequired} from 'util/Types';

import {ResultBlobGraphQL} from '../../../../graphql-fragments/results/__generated/ResultBlobGraphQL';
import {SchemaFieldSelectorGraphQL} from '../../../../graphql-fragments/schemas/__generated/SchemaFieldSelectorGraphQL';
import {SchemaFieldSelectorOptionGraphQL} from '../../../../graphql-fragments/schemas/__generated/SchemaFieldSelectorOptionGraphQL';
import {TagSchemaListingGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';
import {TagSchemaListingWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {TagSchemaChipOrInaccessibleGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaChipOrInaccessibleGraphQL';
import {TagSchemaGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaGraphQL';
import {TagSchemaSummaryGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {TagSchemaValidationErrorLabelSummaryGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaValidationErrorLabelSummaryGraphQL';
import {TagSchemaValidationStatusSummaryGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaValidationStatusSummaryGraphQL';

type TagSchemaChipGraphQL = ExtractByTypename<TagSchemaChipOrInaccessibleGraphQL, 'TagSchemaNode'>;

type ValidationStatusSummaryGraphQL = TagSchemaValidationStatusSummaryGraphQL['validationStatusSummary'];
type ValidationErrorLabelSummaryGraphQL =
  TagSchemaValidationErrorLabelSummaryGraphQL['validationErrorLabelSummary'];

const LABELING_STRATEGIES: Array<RegistryLabelingStrategy> = [
  RegistryLabelingStrategy.NEW_IDS,
  RegistryLabelingStrategy.IDS_FROM_NAMES,
  RegistryLabelingStrategy.DELETE_NAMES,
];

export const TAG_SCHEMA_PERMISSIONS: MakeFieldsRequired<Permissions, 'objectsRegisterable'> = {
  admin: false,
  appendable: true,
  archivable: true,
  definitionDeletable: true,
  definitionEditable: true,
  definitionListable: true,
  objectsCreatable: true,
  objectsRegisterable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const TAG_SCHEMA_PERMISSIONS_GQL: TagSchemaListingGraphQL['permissions'] = {
  __typename: 'PermissionsNode',
  objectsCreatable: true,
  objectsRegisterable: true,
};

export const SCHEMA_FIELD_TEXT = {
  id: 32,
  dbId: 32,
  apiId: 'tsf_123',
  fieldType: FieldType.TEXT,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  name: 'Cat',
  displayName: 'Cat',
  systemName: 'cat',
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
  description: null,
  relatedBenchlingApps: [],
  tooltipText: null,
  unitApiIdentifier: null,
  unitSymbol: null,
} satisfies SchemaField;

// Full fixture for composability. Contains all fields of the GraphQL type.
// Use this to back GraphQL queries that only fetch parts of this object.
export const SCHEMA_FIELD_TEXT_NODE: SchemaFieldNode = {
  __typename: 'SchemaFieldNode',
  ...SCHEMA_FIELD_TEXT,
  activeMigrationFieldStatus: FieldMigrationStatus.NA,
  appliedFieldset: null,
  childEntityLinkConfig: null,
  dependencyStrings: [],
  dependentsSummaries: [],
  formulaDescription: null,
  numFieldValues: 0,
  numMigratedFieldValues: null,
  requiredLinkJson: null,
  unit: null,
};

export const TAG_SCHEMA_SUMMARY: TagSchemaSummary = {
  iconId: FolderItemIcon.Sequence,
  id: 'ts_AawxP6Js',
  name: 'Plasmid',
  archiveRecord: null,
  folderItemType: ModelTypeConstants.SEQUENCE,
  sequenceType: TagSchemaSequenceSchemaType.DNA_SEQUENCE,
  sqlIdentifier: 'plasmid',
  shouldCreateAsOligo: false,
};

export const TAG_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  id: TAG_SCHEMA_SUMMARY.id,
  name: TAG_SCHEMA_SUMMARY.name,
  iconId: 'sequence',
  folderItemType: TagSchemaSupportedFolderItemType.sequence,
  sequenceType: TagSchemaSequenceSchemaType.DNA_SEQUENCE,
  shouldCreateAsOligo: TAG_SCHEMA_SUMMARY.shouldCreateAsOligo,
  sqlIdentifier: TAG_SCHEMA_SUMMARY.sqlIdentifier,
  archiveRecord: null,
};

export const TAG_SCHEMA_SUMMARY_WITH_FIELDS_GQL: TagSchemaSummaryWithFieldsGraphQL = {
  ...TAG_SCHEMA_SUMMARY_GQL,
  fieldsJson: [SCHEMA_FIELD_TEXT],
};

export const TAG_SCHEMA: TagSchema = {
  ...TAG_SCHEMA_SUMMARY,
  authParentOption: AuthParentOption.FOLDER,
  batchSchemaId: null,
  childEntitySchemaSummaries: [],
  constraint: null,
  containableType: TagSchemaContainableType.NONE,
  allFields: [SCHEMA_FIELD_TEXT],
  fields: [SCHEMA_FIELD_TEXT],
  hoistedTagSchemaGraphs: [],
  iconId: FolderItemIcon.Sequence,
  includeRegistryIdInChips: false,
  isContainable: false,
  labelingStrategies: LABELING_STRATEGIES,
  mixtureSchemaConfig: null,
  nameTemplateParts: [],
  prefix: 'p',
  registryId: REGISTRY_SUMMARY.id,
  resultSchema: null,
  showResidues: false,
  shouldCreateAsOligo: false,
  shouldOrderNamePartsBySequence: false,
  sqlIdentifier: 'plasmid',
  useRandomOrgAlias: false,
  useOrganizationCollectionAliasForDisplayLabel: false,
  permissions: TAG_SCHEMA_PERMISSIONS,
  schemaDescription: 'Plasmid description',
};

export const TAG_SCHEMA_GQL: TagSchemaGraphQL = restToGraphQL('TagSchema', TAG_SCHEMA);

// Full fixture for composability. Contains all fields of the GraphQL type.
// Use this to back GraphQL queries that only fetch parts of this object.
export const TAG_SCHEMA_NODE: TagSchemaNode = {
  __typename: 'TagSchemaNode',
  activeMigration: null,
  advancedRules: [],
  allFields: [SCHEMA_FIELD_TEXT_NODE],
  allFieldsJson: [SCHEMA_FIELD_TEXT],
  allFieldsNodes: [SCHEMA_FIELD_TEXT_NODE],
  analyticsDashboardLink: null,
  archiveRecord: null,
  authParentOption: TagSchemaAuthParentOption.FOLDER,
  batchSchema: null,
  childEntityLinkConfig: null,
  childEntityLinkConfigs: [],
  childEntitySchemas: [],
  collaboratorsJson: [],
  constraint: null,
  containableType: TagSchemaContainableTypeEnum.NONE,
  containerNameTemplateParts: [],
  fields: [SCHEMA_FIELD_TEXT_NODE],
  fieldsJson: [SCHEMA_FIELD_TEXT],
  folderItemType: TagSchemaSupportedFolderItemType.basic_folder_item,
  hasAtLeastOneValidationRule: false,
  hasData: false,
  hoistedTagSchemaGraphsJson: [],
  hoistedTagSchemaOrderedPathOptionsJson: null,
  iconId: 'entity',
  id: 'ts_testSchema',
  includeRegistryIdInChips: false,
  inheritedCollaborations: [],
  interfaceConfigs: [],
  interfaces: [],
  isContainable: false,
  itemType: TagSchemaItemType.BASIC_FOLDER_ITEM,
  labelingStrategies: [],
  labelingStrategiesOverrideSource: null,
  mixtureSchemaConfig: null,
  name: 'Test Schema',
  nameTemplateParts: [],
  nameTemplates: [],
  nameTemplatesOverrideSource: null,
  numItems: 0,
  parentLineagePaths: {__typename: 'JSONScalarBox', value: null},
  permissions: FULL_PERMISSIONS_GRAPHQL,
  possibleNameTemplateParts: [],
  prefix: 'TEST',
  prefixSource: null,
  registry: REGISTRY_NODE,
  relatedBenchlingApps: [],
  schemaDescription: '',
  schemaField: {__typename: 'InaccessibleInfoNode', canRequestAccess: false, invalidId: ''},
  sequenceType: null,
  shouldCreateAsOligo: false,
  shouldOrderNamePartsBySequence: false,
  showResidues: false,
  simpleRules: [],
  spreadsheetColumnsMappings: [],
  sqlIdentifier: 'test_schema',
  useOrganizationCollectionAliasForDisplayLabel: false,
  useRandomOrgAlias: false,
  validationErrorLabelSummary: null,
  validationRules: [],
  validationRulesTemplateAndInputsMapping: {},
  validationStatusSummary: null,
};

export const TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_SUMMARY_GQL,
  prefix: TAG_SCHEMA.prefix,
  allFields: [SCHEMA_FIELD_TEXT],
  fields: [SCHEMA_FIELD_TEXT],
  isContainable: TAG_SCHEMA.isContainable,
  permissions: TAG_SCHEMA_PERMISSIONS_GQL,
  registry: {__typename: 'RegistryNode', id: TAG_SCHEMA.registryId},
  containableType: TagSchemaContainableType.NONE,
  labelingStrategies: LABELING_STRATEGIES,
  mixtureSchemaConfig: null,
  childEntitySchemas: [],
  childEntityLinkConfigs: [],
  interfaces: [],
};

export const BFI_TAG_SCHEMA_SUMMARY: TagSchemaSummary = {
  archiveRecord: null,
  folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
  iconId: 'bacteria',
  id: 'ts_mvfiNbmK',
  name: 'Strain',
  sequenceType: null,
  shouldCreateAsOligo: false,
  sqlIdentifier: 'strain',
};

export const BFI_TAG_SCHEMA: TagSchema = {
  ...BFI_TAG_SCHEMA_SUMMARY,
  authParentOption: AuthParentOption.FOLDER,
  batchSchemaId: null,
  childEntitySchemaSummaries: [],
  constraint: null,
  containableType: TagSchemaContainableType.NONE,
  allFields: [],
  fields: [],
  hoistedTagSchemaGraphs: [],
  includeRegistryIdInChips: false,
  isContainable: false,
  labelingStrategies: LABELING_STRATEGIES,
  mixtureSchemaConfig: null,
  nameTemplateParts: [],
  prefix: 's',
  registryId: REGISTRY_SUMMARY.id,
  resultSchema: null,
  shouldOrderNamePartsBySequence: false,
  showResidues: false,
  useRandomOrgAlias: false,
  useOrganizationCollectionAliasForDisplayLabel: false,
  permissions: TAG_SCHEMA_PERMISSIONS,
  schemaDescription: 'Strain description',
};

export const BFI_TAG_SCHEMA_GQL: TagSchemaGraphQL = restToGraphQL('TagSchema', BFI_TAG_SCHEMA);

export const BFI_TAG_SCHEMA_WITH_INTERFACE_GQL = {
  ...BFI_TAG_SCHEMA_GQL,
  interfaces: [
    {
      __typename: 'SchemaInterfaceNode',
      id: 'intrfc_123',
      name: 'Schema Interface',
    },
  ],
};

export const BFI_TAG_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  id: BFI_TAG_SCHEMA.id,
  name: BFI_TAG_SCHEMA.name,
  iconId: BFI_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.basic_folder_item,
  sequenceType: null,
  shouldCreateAsOligo: false,
  sqlIdentifier: BFI_TAG_SCHEMA.sqlIdentifier,
  archiveRecord: null,
};

export const BFI_TAG_SCHEMA_LISTING_GQL: TagSchemaListingGraphQL = {
  __typename: 'TagSchemaNode',
  id: BFI_TAG_SCHEMA.id,
  name: BFI_TAG_SCHEMA.name,
  iconId: BFI_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.basic_folder_item,
  sequenceType: null,
  shouldCreateAsOligo: false,
  permissions: TAG_SCHEMA_PERMISSIONS_GQL,
};

export const BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_GQL,
  fields: BFI_TAG_SCHEMA.fields,
  allFields: BFI_TAG_SCHEMA.allFields,
  prefix: BFI_TAG_SCHEMA_GQL.prefix,
  sqlIdentifier: BFI_TAG_SCHEMA_GQL.sqlIdentifier,
  isContainable: BFI_TAG_SCHEMA_GQL.isContainable,
  containableType: BFI_TAG_SCHEMA_GQL.containableType,
  labelingStrategies: BFI_TAG_SCHEMA_GQL.labelingStrategies,
  registry: BFI_TAG_SCHEMA_GQL.registry,
  mixtureSchemaConfig: BFI_TAG_SCHEMA_GQL.mixtureSchemaConfig,
  childEntitySchemas: BFI_TAG_SCHEMA_GQL.childEntitySchemas,
  archiveRecord: BFI_TAG_SCHEMA_GQL.archiveRecord,
  childEntityLinkConfigs: [],
  interfaces: [],
};

export const NON_CREATABLE_BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: 'ts_noncreatable',
  permissions: {
    ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL.permissions,
    objectsCreatable: false,
  },
};

export const BFI_TAG_SCHEMA_CHIP_GQL: TagSchemaChipGraphQL = {
  __typename: 'TagSchemaNode',
  id: BFI_TAG_SCHEMA.id,
  name: BFI_TAG_SCHEMA.name,
  iconId: BFI_TAG_SCHEMA.iconId,
  archiveRecord: null,
};

export const BFI_TAG_SCHEMA_WITH_ITEM_TYPE_GQL = {
  ...BFI_TAG_SCHEMA_GQL,
  itemType: TagSchemaItemType.BASIC_FOLDER_ITEM,
};

export const BFI_TAG_SCHEMA_2: TagSchema = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_lskdjfew',
  name: 'Strain 2',
};

export const BFI_TAG_SCHEMA_2_GQL = {
  ...BFI_TAG_SCHEMA_GQL,
  id: BFI_TAG_SCHEMA_2.id,
  name: BFI_TAG_SCHEMA_2.name,
};

export const BFI_TAG_SCHEMA_2_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  ...BFI_TAG_SCHEMA_SUMMARY_GQL,
  id: BFI_TAG_SCHEMA_2.id,
  name: BFI_TAG_SCHEMA_2.name,
};

export const BFI_TAG_SCHEMA_2_LISTING_GQL: TagSchemaListingGraphQL = {
  __typename: 'TagSchemaNode',
  id: BFI_TAG_SCHEMA_2.id,
  name: BFI_TAG_SCHEMA_2.name,
  iconId: BFI_TAG_SCHEMA_2.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.basic_folder_item,
  sequenceType: null,
  shouldCreateAsOligo: false,
  permissions: TAG_SCHEMA_PERMISSIONS_GQL,
};

export const BFI_TAG_SCHEMA_2_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  ...BFI_TAG_SCHEMA_2_LISTING_GQL,
  fields: BFI_TAG_SCHEMA_2.fields,
  allFields: BFI_TAG_SCHEMA_2.allFields,
};

export const BFI_TAG_SCHEMA_2_CHIP_GQL: TagSchemaChipGraphQL = {
  __typename: 'TagSchemaNode',
  id: BFI_TAG_SCHEMA_2.id,
  name: BFI_TAG_SCHEMA_2.name,
  iconId: BFI_TAG_SCHEMA_2.iconId,
  archiveRecord: null,
};

export const BFI_TAG_SCHEMA_WITH_BATCH_SCHEMA: TagSchema = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_kmpaqif',
  batchSchemaId: 'batsch_Fj21ppr',
  containableType: TagSchemaContainableType.BATCH,
};

export const BFI_TAG_SCHEMA_WITH_BATCH_SCHEMA_GQL: TagSchemaGraphQL = {
  ...restToGraphQL('TagSchema', BFI_TAG_SCHEMA_WITH_BATCH_SCHEMA),
  batchSchema: {
    __typename: 'BatchSchemaNode',
    id: BFI_TAG_SCHEMA_WITH_BATCH_SCHEMA.batchSchemaId!,
  },
};

export const BFI_TAG_SCHEMA_2_WITH_ITEM_TYPE_GQL = {
  ...BFI_TAG_SCHEMA_2_GQL,
  itemType: TagSchemaItemType.BASIC_FOLDER_ITEM,
};

export const PROTEIN_TAG_SCHEMA: TagSchema = {
  allFields: [],
  archiveRecord: null,
  authParentOption: AuthParentOption.FOLDER,
  batchSchemaId: null,
  childEntitySchemaSummaries: [],
  constraint: {
    areUniqueResiduesCaseSensitive: false,
    id: 2,
    fields: [],
    status: TagSchemaConstraintStatus.ACTIVE,
    uniqueResidues: true,
    uniqueCanonicalSmiles: false,
    source: null,
  },
  containableType: TagSchemaContainableType.ENTITY,
  fields: [],
  folderItemType: ModelTypeConstants.PROTEIN,
  hoistedTagSchemaGraphs: [],
  iconId: 'protein',
  id: 'ts_2uWT9kUH',
  includeRegistryIdInChips: false,
  isContainable: true,
  labelingStrategies: [RegistryLabelingStrategy.KEEP_NAMES, RegistryLabelingStrategy.NEW_IDS],
  mixtureSchemaConfig: null,
  name: 'Chain',
  nameTemplateParts: [],
  prefix: 'M',
  registryId: 'src_Y4EXiECr',
  resultSchema: null,
  sequenceType: null,
  shouldCreateAsOligo: false,
  shouldOrderNamePartsBySequence: false,
  showResidues: false,
  sqlIdentifier: 'chain',
  useOrganizationCollectionAliasForDisplayLabel: false,
  useRandomOrgAlias: true,
  permissions: TAG_SCHEMA_PERMISSIONS,
  schemaDescription: 'Chain description',
};

export const PROTEIN_TAG_SCHEMA_GQL = restToGraphQL('TagSchema', PROTEIN_TAG_SCHEMA);

export const PROTEIN_TAG_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  id: PROTEIN_TAG_SCHEMA.id,
  name: PROTEIN_TAG_SCHEMA.name,
  iconId: PROTEIN_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.protein,
  sequenceType: PROTEIN_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: PROTEIN_TAG_SCHEMA.shouldCreateAsOligo,
  sqlIdentifier: PROTEIN_TAG_SCHEMA.sqlIdentifier,
  archiveRecord: null,
};

export const PROTEIN_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: PROTEIN_TAG_SCHEMA.id,
  name: PROTEIN_TAG_SCHEMA.name,
  folderItemType: TagSchemaSupportedFolderItemType.protein,
  sequenceType: PROTEIN_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: PROTEIN_TAG_SCHEMA.shouldCreateAsOligo,
  fields: PROTEIN_TAG_SCHEMA.fields,
  allFields: PROTEIN_TAG_SCHEMA.allFields,
  isContainable: PROTEIN_TAG_SCHEMA.isContainable,
  containableType: TagSchemaContainableType.ENTITY,
  registry: {__typename: 'RegistryNode', id: PROTEIN_TAG_SCHEMA.registryId},
};

export const PROTEIN_TAG_SCHEMA_CHIP_GQL: TagSchemaChipGraphQL = {
  __typename: 'TagSchemaNode',
  id: PROTEIN_TAG_SCHEMA.id,
  name: PROTEIN_TAG_SCHEMA.name,
  iconId: PROTEIN_TAG_SCHEMA.iconId,
  archiveRecord: null,
};

export const TAG_SCHEMA_FOR_FINDER_ITEM: TagSchema = {
  allFields: [],
  archiveRecord: null,
  authParentOption: AuthParentOption.FOLDER,
  batchSchemaId: null,
  childEntitySchemaSummaries: [],
  constraint: null,
  containableType: TagSchemaContainableType.NONE,
  fields: [],
  folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
  hoistedTagSchemaGraphs: [],
  iconId: 'notdef',
  id: 'foobar',
  includeRegistryIdInChips: false,
  isContainable: false,
  labelingStrategies: LABELING_STRATEGIES,
  mixtureSchemaConfig: null,
  name: 'foobar',
  nameTemplateParts: [],
  prefix: 'foobar',
  registryId: 'foobar',
  resultSchema: null,
  sequenceType: null,
  shouldCreateAsOligo: false,
  shouldOrderNamePartsBySequence: false,
  showResidues: false,
  sqlIdentifier: 'foobar',
  useOrganizationCollectionAliasForDisplayLabel: false,
  useRandomOrgAlias: false,
  permissions: TAG_SCHEMA_PERMISSIONS,
  schemaDescription: 'Foobar description',
};

export const TAG_SCHEMA_FOR_FINDER_ITEM_GQL: TagSchemaGraphQL = {
  ...TAG_SCHEMA_GQL,
  folderItemType: TagSchemaSupportedFolderItemType.basic_folder_item,
  id: TAG_SCHEMA_FOR_FINDER_ITEM.id,
  name: TAG_SCHEMA_FOR_FINDER_ITEM.name,
  prefix: TAG_SCHEMA_FOR_FINDER_ITEM.prefix,
  registry: {__typename: 'RegistryNode', id: TAG_SCHEMA_FOR_FINDER_ITEM.registryId},
  iconId: TAG_SCHEMA_FOR_FINDER_ITEM.iconId,
};

export const TAG_SCHEMA_FOR_ENTRY: TagSchema = {
  ...TAG_SCHEMA_FOR_FINDER_ITEM,
  folderItemType: ModelTypeConstants.ENTRY,
  id: 'ts_entry_tag_schema',
  name: 'Entry tag schema',
  prefix: 'entryts',
  registryId: 'src_entryts',
  sqlIdentifier: 'entryts',
  iconId: FolderItemIcon.Entry,
};

export const TAG_SCHEMA_FOR_ENTRY_GQL: TagSchemaGraphQL = {
  ...TAG_SCHEMA_FOR_FINDER_ITEM_GQL,
  folderItemType: TagSchemaSupportedFolderItemType.entry,
  id: TAG_SCHEMA_FOR_ENTRY.id,
  name: TAG_SCHEMA_FOR_ENTRY.name,
  prefix: TAG_SCHEMA_FOR_ENTRY.prefix,
  registry: {__typename: 'RegistryNode', id: TAG_SCHEMA_FOR_ENTRY.registryId},
  iconId: TAG_SCHEMA_FOR_ENTRY.iconId,
};

export const TAG_SCHEMA_FOR_ENTRY_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  folderItemType: TagSchemaSupportedFolderItemType.entry,
  id: TAG_SCHEMA_FOR_ENTRY.id,
  name: TAG_SCHEMA_FOR_ENTRY.name,
  prefix: TAG_SCHEMA_FOR_ENTRY.prefix,
  registry: {__typename: 'RegistryNode', id: TAG_SCHEMA_FOR_ENTRY.registryId},
  iconId: FolderItemIcon.Entry,
};

export const BFI_TAG_SCHEMA_ARCHIVED: TagSchema = {
  ...BFI_TAG_SCHEMA,
  archiveRecord: {purpose: 'Made in error'},
};

export const ENTRY_SCHEMA: TagSchema = {
  archiveRecord: null,
  authParentOption: AuthParentOption.FOLDER,
  batchSchemaId: null,
  childEntitySchemaSummaries: [],
  constraint: null,
  containableType: TagSchemaContainableType.NONE,
  allFields: [],
  fields: [],
  folderItemType: ModelTypeConstants.ENTRY,
  hoistedTagSchemaGraphs: [],
  iconId: FolderItemIcon.Entry,
  id: 'ts_zItHFFpc',
  includeRegistryIdInChips: false,
  isContainable: false,
  labelingStrategies: LABELING_STRATEGIES,
  mixtureSchemaConfig: null,
  name: 'QC Entry',
  nameTemplateParts: [],
  prefix: 'qc',
  registryId: 'src_2K7OuOWF',
  resultSchema: null,
  sequenceType: null,
  shouldCreateAsOligo: false,
  shouldOrderNamePartsBySequence: false,
  showResidues: false,
  sqlIdentifier: 'qc_entry',
  useRandomOrgAlias: true,
  useOrganizationCollectionAliasForDisplayLabel: false,
  permissions: TAG_SCHEMA_PERMISSIONS,
  schemaDescription: '',
};

export const ENTRY_SCHEMA_GQL = restToGraphQL('TagSchema', ENTRY_SCHEMA);

export const ENTRY_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  archiveRecord: null,
  folderItemType: TagSchemaSupportedFolderItemType.entry,
  iconId: ENTRY_SCHEMA.iconId,
  id: ENTRY_SCHEMA.id,
  name: ENTRY_SCHEMA.name,
  sequenceType: ENTRY_SCHEMA.sequenceType,
  shouldCreateAsOligo: ENTRY_SCHEMA.shouldCreateAsOligo,
  sqlIdentifier: ENTRY_SCHEMA.sqlIdentifier,
};

export const ENTRY_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  ...ENTRY_SCHEMA_SUMMARY_GQL,
  fields: ENTRY_SCHEMA.fields,
  allFields: ENTRY_SCHEMA.allFields,
};

export const SCHEMA_FIELD_DATA_TYPE_TEXT: SchemaFieldDataType = pick(
  SCHEMA_FIELD_TEXT,
  'fieldType',
  'requiredLink',
  'isMulti',
  'isRequired'
);

export const SCHEMA_FIELD = SCHEMA_FIELD_TEXT;

export const SCHEMA_FIELD_TEXT_2: SchemaField = {
  id: 33,
  dbId: 33,
  apiId: 'tsf_234',
  fieldType: FieldType.TEXT,
  name: 'Status',
  displayName: 'Status',
  systemName: 'status',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
  description: null,
  relatedBenchlingApps: [],
  tooltipText: null,
};

export const SCHEMA_FIELD_TEXT_WITH_TOOLTIP: SchemaField = {
  id: 132,
  dbId: 132,
  apiId: 'tsf_132',
  fieldType: FieldType.TEXT,
  description: null,
  tooltipText: 'this is a tooltip',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  name: 'Field with description',
  displayName: 'Field with description',
  systemName: 'field_with_description',
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
  relatedBenchlingApps: [],
};

export const SCHEMA_TAG_TEXT: SchemaTag = {
  name: 'Cat',
  tagSchemaField: SCHEMA_FIELD_TEXT,
  url: '',
  value: 'Nacho',
};

export const UNAFFILIATED_SCHEMA_TAG_TEXT: UnaffiliatedSchemaTag = {
  tagSchemaField: SCHEMA_FIELD_DATA_TYPE_TEXT,
  url: '',
  value: 'Dangermouse',
};

export const SCHEMA_TAG_TEXT_WITH_TOOLTIP: SchemaTag = {
  name: 'Description Tag',
  tagSchemaField: SCHEMA_FIELD_TEXT_WITH_TOOLTIP,
  url: '',
  value: 'Has a field tooltip',
};

export const SCHEMA_TAG_TEXT_2: SchemaTag = {
  name: 'Status',
  tagSchemaField: SCHEMA_FIELD_TEXT_2,
  url: '',
  value: 'In review',
};

export const SCHEMA_FIELD_LONG_TEXT: SchemaField = {
  id: 35,
  dbId: 35,
  apiId: 'tsf_SDj19skx',
  fieldType: FieldType.LONG_TEXT,
  name: 'Description',
  displayName: 'Description',
  systemName: 'description',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_LONG_TEXT: SchemaTag = {
  name: 'Description',
  tagSchemaField: SCHEMA_FIELD_LONG_TEXT,
  url: '',
  value: 'This is my really really really really really long description.',
};

export const SCHEMA_FIELD_INTEGER: SchemaField = {
  id: 24,
  apiId: 'tsf_abDSlx22z',
  dbId: 24,
  fieldType: FieldType.INTEGER,
  name: 'my_count',
  displayName: 'My Count',
  systemName: 'my_count',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_INTEGER: SchemaTag = {
  name: 'my_count',
  tagSchemaField: SCHEMA_FIELD_INTEGER,
  integerValue: 42,
  value: '42',
};

export const SCHEMA_FIELD_INTEGER_2: SchemaField = {
  id: 224,
  apiId: 'tsf_abDSlx22z',
  dbId: 224,
  fieldType: FieldType.INTEGER,
  name: 'my_count_2',
  displayName: 'My Count 2',
  systemName: 'my_count_2',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_INTEGER_2 = {
  id: 242,
  name: 'my_count_2',
  tagSchemaField: SCHEMA_FIELD_INTEGER_2,
  integerValue: 242,
  value: '242',
};

export const UNIT_AWARE_SCHEMA_FIELD_INTEGER: SchemaField = {
  id: 24,
  apiId: 'tsf_abDSlx22z',
  dbId: 24,
  fieldType: FieldType.INTEGER,
  name: 'my_count',
  displayName: 'My Count',
  systemName: 'my_count',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
  unitApiIdentifier: 'unit_abcd1234',
};

export const SCHEMA_FIELD_FLOAT: SchemaField = {
  id: 34,
  apiId: 'tsf_NvMrc3y7',
  dbId: 34,
  fieldType: FieldType.FLOAT,
  name: 'Temperature',
  displayName: 'Temperature',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  systemName: 'temperature',
  archiveRecord: null,
  description: null,
  tooltipText: null,
};

export const SCHEMA_TAG_FLOAT: SchemaTag = {
  name: 'Temperature',
  tagSchemaField: SCHEMA_FIELD_FLOAT,
  floatValue: 56.1,
  value: '56.1',
  rawValue: '56.1',
};

export const SCHEMA_TAG_FLOAT_WITH_DECIMAL_PRECISION: SchemaTag = {
  name: 'Temperature',
  tagSchemaField: SCHEMA_FIELD_FLOAT,
  floatValue: 56.123,
  value: '56.1',
  rawValue: '5.6123e+1',
};

export const SCHEMA_FIELD_FLOAT_2: SchemaField = {
  id: 199,
  apiId: 'tsf_float2',
  dbId: 199,
  fieldType: FieldType.FLOAT,
  name: 'Temperature',
  displayName: 'Temperature',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  systemName: 'temperature',
  archiveRecord: null,
};

export const SCHEMA_FIELD_FLOAT_ARCHIVED: SchemaField = {
  id: 212,
  apiId: 'tsf_float2',
  dbId: 212,
  fieldType: FieldType.FLOAT,
  name: 'Temperature',
  displayName: 'Temperature',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  systemName: 'temperature',
  archiveRecord: {purpose: 'made in error'},
};

export const SCHEMA_TAG_FLOAT_2: SchemaTag = {
  name: 'Temperature',
  tagSchemaField: SCHEMA_FIELD_FLOAT_2,
  floatValue: 564.1,
  value: '564.1',
};

export const SCHEMA_FIELD_DATE: SchemaField = {
  id: 34,
  apiId: 'tsf_BaYwxi29',
  dbId: 34,
  fieldType: FieldType.DATE,
  name: 'Expiration Date',
  displayName: 'Expiration Date',
  systemName: 'expiration_date',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_DATE: SchemaTag = {
  name: 'Expiration Date',
  tagSchemaField: SCHEMA_FIELD_DATE,
  dateValue: '2017-04-20',
  value: '2017-04-20',
};

export const SCHEMA_FIELD_DATETIME: SchemaField = {
  id: 34,
  apiId: 'tsf_pbN2kksn',
  dbId: 34,
  fieldType: FieldType.DATETIME,
  name: 'Expiration Date',
  displayName: 'Expiration Date',
  systemName: 'expiration_date',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_DATETIME: SchemaTag & {datetimeValue: string} = {
  name: 'Expiration Date',
  tagSchemaField: SCHEMA_FIELD_DATETIME,
  datetimeValue: '2020-01-01T01:01:01-0800',
  value: '2020-01-01 01:01:01 AM',
};

export const SCHEMA_FIELD_BIOENTITY_LINK_GENERIC: SchemaField = {
  id: 9938,
  apiId: 'tsf_wooo22pv',
  dbId: 9938,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'Some entity',
  displayName: 'Some entity',
  systemName: 'some_entity',
  requiredLink: {
    tagSchema: null,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_BIOENTITY_LINK: BioentityLinkFieldWithTagSchema = {
  id: 36,
  apiId: 'tsf_jel1lsys',
  dbId: 36,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'Strain',
  displayName: 'Strain',
  systemName: 'strain',
  requiredLink: {
    tagSchema: BFI_TAG_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_OTHER_BIOENTITY_SCHEMA: SchemaField = {
  ...SCHEMA_FIELD_BIOENTITY_LINK,
  apiId: 'tsf_n0nc0nt41n',
  name: 'Non Containable',
  displayName: 'Non Containable',
  systemName: 'non_containable',
  requiredLink: {
    tagSchema: BFI_TAG_SCHEMA_2,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
};

export const SCHEMA_FIELD_BIOENTITY_LINK_2: SchemaField = {
  id: 37,
  apiId: 'tsf_jel1lsyt',
  dbId: 37,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'Plasmid 2',
  displayName: 'Plasmid 2',
  systemName: 'plasmid_2',
  requiredLink: {
    tagSchema: BFI_TAG_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_BIOENTITY_CONVERTED_FROM_LINK: BioentityLinkFieldWithTagSchema = {
  id: 38,
  apiId: 'tsf_jel1lsyscf',
  dbId: 38,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: true,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  name: 'Converted from',
  displayName: 'Converted from',
  systemName: 'converted_from',
  requiredLink: {
    tagSchema: BFI_TAG_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SINGLE_SCHEMA_FIELD_BIOENTITY_LINK: BioentityLinkFieldWithTagSchema = {
  id: 39,
  apiId: 'tsf_jel1lzaz',
  dbId: 39,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  name: 'Strain',
  displayName: 'Strain',
  systemName: 'strain',
  requiredLink: {
    tagSchema: BFI_TAG_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_FOLDER_ITEM_LINK: SchemaField = {
  id: 36,
  apiId: 'tsf_sp2WSayY',
  dbId: 36,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'My Entity',
  displayName: 'My Entity',
  systemName: 'my_entity',
  requiredLink: {
    folderItemType: 'basic_folder_item',
    tagSchema: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_PART_LINK: SchemaField = {
  fieldType: FieldType.PART_LINK,
  id: 7,
  apiId: 'tsf_tesISLms',
  dbId: 7,
  name: 'Resistance',
  displayName: 'Resistance',
  systemName: 'resistance',
  isMulti: true,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  requiredLink: {
    tagSchema: {
      folderItemType: ModelTypeConstants.SEQUENCE,
      iconId: FolderItemIcon.Sequence,
      id: 'ts_9oChXdm8',
      name: 'Part Link Resistance',
      sqlIdentifier: 'part_link_resistance',
      sequenceType: TagSchemaSequenceSchemaType.DNA_SEQUENCE,
      shouldCreateAsOligo: false,
    },
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_WITH_INTERFACE: SchemaField = {
  archiveRecord: null,
  id: 8,
  apiId: 'tsf_YF1Uc3Fh',
  dbId: 8,
  systemName: 'coat_color',
  displayName: 'Coat color',
  description: null,
  tooltipText: null,
  fieldType: FieldType.TEXT,
  isMulti: false,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isUneditable: false,
  requiredLink: null,
  name: 'Coat color',
  appliedFieldset: {
    name: 'Rodent',
    id: 'intrfc_Oh8fbUC8',
  },
};

export const SCHEMA_FIELD_WITH_STATIC_INTERFACE: SchemaField = {
  ...SCHEMA_FIELD_WITH_INTERFACE,
  id: 9,
  apiId: 'tsf_staticInterface',
  dbId: 9,
  systemName: 'antibody_domain',
  displayName: 'Antibody domain',
  name: 'Antibody domain',
  appliedFieldset: {
    name: 'AA Domain',
    id: 'ssi_aadomain',
  },
};

export const SCHEMA_INTERFACE: SchemaInterface = {
  id: 'intrfc_In9CyCpP',
  name: 'Schema Interface',
  archiveRecord: null,
};

export const STATIC_SCHEMA_INTERFACE: SchemaInterface = {
  id: 'ssi_trialsample',
  name: 'Trial Sample',
  archiveRecord: null,
};

export const SCHEMA_INTERFACE_EXTENDING_SCHEMA: SchemaInterfaceExtendingSchema = {
  ...SCHEMA_INTERFACE,
  baseBioentityType: null,
  allFields: [SCHEMA_FIELD_INTEGER],
  fields: [SCHEMA_FIELD_INTEGER],
};

export const SCHEMA_FIELD_INTERFACE_LINK: BioentityLinkFieldWithSchemaInterface = {
  id: 38,
  apiId: 'tsf_interfish',
  dbId: 38,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'Sample',
  displayName: 'Sample ',
  systemName: 'sample',
  requiredLink: {
    tagSchema: null,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: SCHEMA_INTERFACE,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_STATIC_SCHEMA_INTERFACE_LINK: BioentityLinkFieldWithSchemaInterface = {
  id: 39,
  apiId: 'ssi_trialsample',
  dbId: 39,
  fieldType: FieldType.BIOENTITY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'Sample',
  displayName: 'Sample ',
  systemName: 'sample',
  requiredLink: {
    tagSchema: null,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: STATIC_SCHEMA_INTERFACE,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_BIOENTITY_LINK: SchemaTag = {
  name: 'Strain',
  tagSchemaField: SCHEMA_FIELD_BIOENTITY_LINK,
  targetFiles: [
    {
      url: '/testorg/f/PBDXljD0-enterprise-registry/bfi-teststrain/edit',
      id: 'seq_teststrain',
      name: 'Test strain',
    },
  ],
  targetFileTagSchemaId: 'ts_9oChXdm8',
  url: '',
  value: '',
};

export const SCHEMA_TAG_BIOENTITY_LINK_1: SchemaTag = {
  ...SCHEMA_TAG_BIOENTITY_LINK,
  targetFiles: [],
  value: 'Test strain',
};

export const SCHEMA_TAG_BIOENTITY_LINK_2: SchemaTag = {
  name: 'Plasmid 2',
  tagSchemaField: SCHEMA_FIELD_BIOENTITY_LINK_2,
  targetFiles: [],
  targetFileTagSchemaId: 'ts_9oChXdm8',
  url: '',
  value: 'My plasmid',
};

export const BIOENTITY_TAG_TYPED_VALUE: BioentityLinkSchemaFieldTypedValue = {
  type: FieldType.BIOENTITY_LINK,
  value: '',
  targetFiles: [
    {
      url: '/testorg/f/PBDXljD0-enterprise-registry/bfi-teststrain/edit',
      id: 'seq_teststrain',
      name: 'Test strain',
    },
  ],
};

export const BIOENTITY_TAG_TYPED_VALUE_2: BioentityLinkSchemaFieldTypedValue = {
  type: FieldType.BIOENTITY_LINK,
  value: '',
  targetFiles: [
    {
      url: '/testorg/f/PBDXljD0-enterprise-registry/bfi-anotherstrain/edit',
      id: 'seq_anotherstrain',
      name: 'Another Test strain',
    },
  ],
};

export const SCHEMA_TAG_PART_LINK: SchemaTag = {
  name: 'Resistance',
  tagSchemaField: SCHEMA_FIELD_PART_LINK,
  targetFiles: [
    {
      url: '/testorg/f/PBDXljD0-enterprise-registry/seq-7HT3KyPx-resistance0/edit',
      id: 'seq_7HT3KyPx',
      name: 'resistance0',
    },
  ],
  targetFileTagSchemaId: 'ts_9oChXdm8',
  value: 'resistance0',
};

export const SCHEMA_FIELD_TRANSLATION_LINK: SchemaField = {
  name: 'Chain',
  displayName: 'Chain',
  systemName: 'chain',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: {
    tagSchema: {
      folderItemType: ModelTypeConstants.PROTEIN,
      iconId: 'protein',
      id: 'ts_CVeV2Oxg',
      name: 'Chain',
      sqlIdentifier: 'chain',
      sequenceType: TagSchemaSequenceSchemaType.DNA_SEQUENCE,
      shouldCreateAsOligo: false,
    },
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  fieldType: FieldType.TRANSLATION_LINK,
  id: 5,
  apiId: 'tsf_tRnzskLin',
  dbId: 5,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_FIELD_TRANSLATION_LINK_TO_SSI: SchemaField = {
  ...SCHEMA_FIELD_TRANSLATION_LINK,
  id: 7,
  apiId: 'tsf_kRNmpLqx',
  dbId: 7,
  requiredLink: {
    tagSchema: null,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: {id: 'ssi_link', name: 'Test SSI'},
  },
};

export const SCHEMA_FIELD_TRANSCRIPTION_LINK: SchemaField = {
  name: 'transcription',
  displayName: 'transcription',
  systemName: 'transcription',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: {
    tagSchema: {
      folderItemType: ModelTypeConstants.SEQUENCE,
      iconId: FolderItemIcon.Sequence,
      id: 'ts_CVeV2Oxg',
      name: 'transcription',
      sqlIdentifier: 'transcription',
      sequenceType: TagSchemaSequenceSchemaType.RNA_SEQUENCE,
      shouldCreateAsOligo: false,
    },
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  fieldType: FieldType.TRANSCRIPTION_LINK,
  id: 6,
  apiId: 'tsf_tRnzskLin',
  dbId: 6,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_TRANSLATION_LINK: SchemaTag = {
  name: 'Chain',
  targetFiles: [
    {
      url: '/lpasteur/f/YJPg0JBl-my-protocols/prtn-Y3SL1WqS-m-354990-translation-1-12/edit',
      id: 'prtn_Y3SL1WqS',
      name: 'M-354,990 translation [1-12]',
    },
  ],
  tagSchemaField: SCHEMA_FIELD_TRANSLATION_LINK,
  targetFileTagSchemaId: 'ts_CVeV2Oxg',
  value: 'M-354,990 translation [1-12]',
};

export const SCHEMA_FIELD_AA_PART_LINK: SchemaField = {
  name: 'AA Part',
  displayName: 'AA Part',
  systemName: 'aapart',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: {
    tagSchema: {
      folderItemType: ModelTypeConstants.PROTEIN,
      iconId: FolderItemIcon.Protein,
      id: 'ts_Llh4iAT2',
      name: 'AA Part',
      sqlIdentifier: 'aapart',
      sequenceType: null,
      shouldCreateAsOligo: false,
    },
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  fieldType: FieldType.AA_PART_LINK,
  id: 41,
  apiId: 'tsf_UV6XXBRe',
  dbId: 41,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_AA_PART_LINK: SchemaTag = {
  name: 'Resistance',
  tagSchemaField: SCHEMA_FIELD_AA_PART_LINK,
  targetFiles: [
    {
      url: '/lpasteur/f/YJPg0JBl-my-protocols/prtn-Y3SL1WqS-m-354990-translation-1-12/edit',
      id: 'prtn_Y3SL1WqS',
      name: 'M-354,990 translation [1-12]',
    },
  ],
  targetFileTagSchemaId: 'ts_9oChXdm8',
  value: 'part',
};

export const SCHEMA_FIELD_BASE_MOLECULE_LINK: SchemaField = {
  name: 'Base Molecule',
  displayName: 'Base Molecule',
  systemName: 'base_molecule',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: {
    tagSchema: {
      folderItemType: ModelTypeConstants.MOLECULE,
      iconId: FolderItemIcon.Molecule,
      id: 'ts_baseMoleculeLink',
      name: 'Base Molecule',
      sqlIdentifier: 'base_molecule',
      sequenceType: null,
      shouldCreateAsOligo: false,
    },
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  fieldType: FieldType.BASE_MOLECULE_LINK,
  id: 43,
  apiId: 'tsf_BasMol1',
  dbId: 43,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_BASE_MOLECULE_LINK: SchemaTag = {
  name: 'Base Molecule',
  tagSchemaField: SCHEMA_FIELD_BASE_MOLECULE_LINK,
  targetFiles: [
    {
      url: '/lpasteur/f/YJPg0JBl-my-protocols/mol-Z4TM2WrT-aspirin/edit',
      id: 'mol_Z4TM2WrT',
      name: 'Aspirin',
    },
  ],
  targetFileTagSchemaId: 'ts_baseMoleculeLink',
  value: 'base_molecule',
};

export const SCHEMA_FIELD_GENERIC_BATCH_LINK: SchemaField = {
  apiId: 'tsf_batch4u8',
  archiveRecord: null,
  dbId: 8471,
  displayName: 'Generic batch link',
  fieldType: FieldType.BATCH_LINK,
  id: 8471,
  isComputed: false,
  isConvertedFromLink: false,
  isIntegration: false,
  isMulti: false,
  isParentLink: false,
  isRequired: false,
  isSnapshot: false,
  name: 'Generic batch link',
  requiredLink: {batchSchemaGraph: null, batchSchema: null},
  systemName: '',
};

const BATCH_SCHEMA = {
  archiveRecord: null,
  fileTypeName: 'Strain',
  id: 'batsch_uyS3GhHV',
  sqlIdentifier: 'strain$batch',
  name: 'Strain Batch',
  namingScheme: BatchSchemaNamingScheme.BIOENTITY_NAME,
  tagSchemaId: 'ts_Fonllcne',
  allFields: [],
  fields: [],
  hidden: false,
};

export const SCHEMA_FIELD_REQUIRED_LINK_BATCH_LINK: SchemaField = {
  apiId: 'tsf_batchjv1',
  archiveRecord: null,
  dbId: 8472,
  displayName: 'Generic batch link',
  fieldType: FieldType.BATCH_LINK,
  id: 8472,
  isComputed: false,
  isConvertedFromLink: false,
  isIntegration: false,
  isMulti: false,
  isParentLink: false,
  isRequired: false,
  isSnapshot: false,
  name: 'Required link batch link',
  requiredLink: {batchSchemaGraph: null, batchSchema: BATCH_SCHEMA},
  systemName: '',
};

export const SCHEMA_FIELD_BATCH_LINK_WITH_LINEAGE: SchemaField = {
  fieldType: FieldType.BATCH_LINK,
  id: 50,
  apiId: 'tsf_baXlinks',
  dbId: 50,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  name: 'batch link',
  displayName: 'batch link',
  systemName: 'batch_link',
  requiredLink: {
    batchSchema: BATCH_SCHEMA,
    batchSchemaGraph: {
      id: 4,
      edges: [
        {
          from: {
            folderItemType: 'basic_folder_item',
            id: 'ts_Fonllcne',
            name: 'Strain',
          },
          id: 2,
          apiId: 'tsf_abc',
          name: 'edge2',
          systemName: 'edge2',
          to: {
            folderItemType: 'sequence',
            id: 'ts_WudDIIU3',
            name: 'Plasmid',
          },
        },
      ],
    },
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_BATCH_LINK: SchemaTag = {
  name: 'batch link',
  tagSchemaField: SCHEMA_FIELD_BATCH_LINK_WITH_LINEAGE,
  targetBatches: [
    {
      fileId: 'bfi_KKpoLlaC',
      iconId: 'bacteria',
      id: 'bat_LGSSjgIK',
      name: 'sBN101-20170330',
    },
    {
      fileId: 'bfi_KKpoLlaC',
      iconId: 'bacteria',
      id: 'bat_jmZ9gQT9',
      name: 'sBN101-20170330-2',
    },
    {
      fileId: 'bfi_KKpoLlaC',
      iconId: 'bacteria',
      id: 'bat_Ahj39Ade',
      name: 'sBN101-20170330',
    },
  ],
  value: 'sBN101-20170330, sBN101-20170330-2, sBN101-20170330',
};

export const SCHEMA_TAG_SINGLE_BATCH_LINK: SchemaTag = {
  name: 'batch link',
  tagSchemaField: SCHEMA_FIELD_BATCH_LINK_WITH_LINEAGE,
  targetBatches: [
    {
      fileId: 'bfi_KKpoLlaC',
      iconId: 'bacteria',
      id: 'bat_LGSSjgIK',
      name: 'sBN101-20170330',
    },
  ],
  value: 'sBN101-20170330',
};

export const SCHEMA_FIELD_STORABLE_LINK: SchemaField & {requiredLink: RequiredLinkStorableSchema} = {
  fieldType: FieldType.STORABLE_LINK,
  id: 71,
  apiId: 'tsf_sto1FBls',
  dbId: 71,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  name: 'storable link',
  displayName: 'storable link',
  systemName: 'storable_link',
  isMulti: true,
  requiredLink: {
    storableSchema: TUBE_CONTAINER_SCHEMA_SUMMARY,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const PLATE_SCHEMA_SUMMARY: StorableSchemaSummary = {
  archiveRecord: null,
  iconId: 'vial',
  id: 'pltsch_MQ6ZYOql',
  name: 'Tube',
  prefix: 'T',
  registryId: REGISTRY.id,
  sqlIdentifier: 'tube',
};

export const PLATE_SCHEMA_FIELD_STORABLE_LINK: SchemaField & {requiredLink: RequiredLinkStorableSchema} = {
  fieldType: FieldType.STORABLE_LINK,
  id: 71,
  apiId: 'tsf_sto1FBls',
  dbId: 71,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  name: 'storable link',
  displayName: 'storable link',
  systemName: 'storable_link',
  isMulti: true,
  requiredLink: {
    storableSchema: PLATE_SCHEMA_SUMMARY,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_STORABLE_LINK: SchemaTag = {
  name: 'storable link',
  tagSchemaField: SCHEMA_FIELD_STORABLE_LINK,
  targetStorables: [
    {
      iconId: 'vial',
      id: 'con_wyRPbzRx',
      name: 'V001',
    },
    {
      iconId: 'bacteria',
      id: 'con_FYkCThOd',
      name: 'V002',
    },
  ],
  value: 'V001, V002',
};

export const STORABLE_TAG_TYPED_VALUE: StorableLinkSchemaFieldTypedValue = {
  type: FieldType.STORABLE_LINK,
  value: '',
  targetStorables: [
    {
      iconId: 'vial',
      id: 'con_wyRPbzRx',
      name: 'V001',
    },
    {
      iconId: 'bacteria',
      id: 'con_FYkCThOd',
      name: 'V002',
    },
  ],
};

export const STORABLE_TAG_TYPED_VALUE_PLATES: StorableLinkSchemaFieldTypedValue = {
  type: FieldType.STORABLE_LINK,
  value: '',
  targetStorables: [
    {
      iconId: ModelTypeConstants.PLATE,
      id: 'plt_UZfLlswP',
      name: 'Plate 1',
    },
    {
      iconId: ModelTypeConstants.PLATE,
      id: 'plt_SuDTaebA',
      name: 'Plate 2',
    },
  ],
};

export const STORABLE_TAG_TYPED_VALUE_PLATES_2: StorableLinkSchemaFieldTypedValue = {
  type: FieldType.STORABLE_LINK,
  value: '',
  targetStorables: [
    {
      iconId: ModelTypeConstants.PLATE,
      id: 'plt_fyysSDKS',
      name: 'Plate 3',
    },
  ],
};

export const ENTRY_LINK_TYPED_VALUE: EntryLinkSchemaFieldTypedValue = {
  type: 'ft_entry_link',
  value: '',
  targetEntries: [
    {
      id: 'etr_3jtLQSnn',
      name: 'Entry 1',
    },
  ],
};

export const BATCH_LINK_TYPED_VALUE: BatchLinkSchemaFieldTypedValue = {
  type: 'ft_batch_link',
  value: '',
  targetBatches: [
    {
      id: 'bat_gsiZf7mN',
      name: 'Batch 1',
      fileId: 'bfi_3P8LQfjW',
      iconId: 'basic_folder_item',
    },
  ],
};

export const SCHEMA_FIELD_BLOB_LINK: SchemaField = {
  fieldType: FieldType.BLOB_LINK,
  id: 72,
  apiId: 'tsf_blOWbLin',
  dbId: 72,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'blob_link',
  displayName: 'blob link',
  systemName: 'blob_link',
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const BASIC_BLOB: BenchlingBlob = {
  id: '8ea7dd7a-4242-a7c4-8d88-164728562755',
  name: 'file1.json',
  link_type: 'result_blob',
  url: '/results/blobs/8ea7dd7a-4242-a7c4-8d88-164728562755/s3-url',
  previewFile: null,
  pdfPreviewFile: null,
  uploadStatus: BlobUploadStatus.COMPLETE,
};

export const BLOB_GQL: ResultBlobGraphQL = {
  ...pickDefined(BASIC_BLOB, 'id', 'name', 'url'),
  __typename: 'ResultBlobNode',
  previewFileJson: null,
  pdfPreviewFileJson: null,
  uploadStatus: BlobUploadStatus.COMPLETE,
};

export const BLOB_WITH_PREVIEW_FILE_ABORTED: BenchlingBlob = {
  id: '9da6dc0d-313f-4652-a7cc-525475840633',
  name: 'imageaborted.png',
  url: '/results/blobs/9da6dc0d-313f-4652-a7cc-525475840633/s3-url',
  link_type: 'result_blob',
  previewFile: {
    id: 'efl_ds8f99sd9f',
    previewType: EXTERNAL_FILE_TYPE.IMAGE,
    url: 'https://www.benchling.com/wp-content/uploads/2020/05/Benchling-Preview2.png',
    availablePreviewTypes: ['image'],
  },
  pdfPreviewFile: null,
  uploadStatus: BlobUploadStatus.ABORTED,
};

export const BLOB_WITH_PREVIEW_FILE_ABORTED_GQL: ResultBlobGraphQL = {
  __typename: 'ResultBlobNode',
  id: BLOB_WITH_PREVIEW_FILE_ABORTED.id,
  name: BLOB_WITH_PREVIEW_FILE_ABORTED.name,
  url: BLOB_WITH_PREVIEW_FILE_ABORTED.url,
  previewFileJson: {
    id: BLOB_WITH_PREVIEW_FILE_ABORTED.previewFile!.id,
    previewType: BLOB_WITH_PREVIEW_FILE_ABORTED.previewFile!.previewType,
    url: BLOB_WITH_PREVIEW_FILE_ABORTED.previewFile!.url,
    availablePreviewTypes: BLOB_WITH_PREVIEW_FILE_ABORTED.previewFile!.availablePreviewTypes,
  },
  pdfPreviewFileJson: null,
  uploadStatus: BLOB_WITH_PREVIEW_FILE_ABORTED.uploadStatus!,
};

export const BLOB_WITH_PREVIEW_FILE_1: BenchlingBlob = {
  id: '9da6dc0d-313f-4652-a7cc-525475840632',
  name: 'image1.png',
  url: '/results/blobs/9da6dc0d-313f-4652-a7cc-525475840632/s3-url',
  link_type: 'result_blob',
  previewFile: {
    id: 'efl_ds8f99sd8f',
    previewType: EXTERNAL_FILE_TYPE.IMAGE,
    url: 'https://www.benchling.com/wp-content/uploads/2019/04/Benchling-Preview.png',
    availablePreviewTypes: ['image'],
  },
  pdfPreviewFile: null,
  uploadStatus: BlobUploadStatus.COMPLETE,
};

export const BLOB_WITH_PREVIEW_FILE_1_GQL: ResultBlobGraphQL = {
  __typename: 'ResultBlobNode',
  id: BLOB_WITH_PREVIEW_FILE_1.id,
  name: BLOB_WITH_PREVIEW_FILE_1.name,
  url: BLOB_WITH_PREVIEW_FILE_1.url,
  previewFileJson: {
    id: BLOB_WITH_PREVIEW_FILE_1.previewFile!.id,
    previewType: BLOB_WITH_PREVIEW_FILE_1.previewFile!.previewType,
    url: BLOB_WITH_PREVIEW_FILE_1.previewFile!.url,
    availablePreviewTypes: BLOB_WITH_PREVIEW_FILE_1.previewFile!.availablePreviewTypes,
  },
  pdfPreviewFileJson: null,
  uploadStatus: BLOB_WITH_PREVIEW_FILE_1.uploadStatus!,
};

export const BLOB_WITH_PREVIEW_FILE_2: BenchlingBlob = {
  id: '751aac49-1e6f-4905-9eea-60906832b9e2',
  name: 'image2.png',
  link_type: 'result_blob',
  url: '/results/blobs/751aac49-1e6f-4905-9eea-60906832b9e2/s3-url',
  previewFile: {
    id: 'efl_ksdjfsdf09890',
    previewType: EXTERNAL_FILE_TYPE.IMAGE,
    url: 'https://www.benchling.com/wp-content/uploads/2020/05/Benchling-Series-D-HeaderAsset-2.png',
    availablePreviewTypes: ['image'],
  },
  pdfPreviewFile: null,
  uploadStatus: BlobUploadStatus.COMPLETE,
};

export const BLOB_WITH_PREVIEW_FILE_2_GQL: ResultBlobGraphQL = {
  __typename: 'ResultBlobNode',
  id: BLOB_WITH_PREVIEW_FILE_2.id,
  name: BLOB_WITH_PREVIEW_FILE_2.name,
  url: BLOB_WITH_PREVIEW_FILE_2.url,
  previewFileJson: {
    id: BLOB_WITH_PREVIEW_FILE_2.previewFile!.id,
    previewType: BLOB_WITH_PREVIEW_FILE_2.previewFile!.previewType,
    url: BLOB_WITH_PREVIEW_FILE_2.previewFile!.url,
    availablePreviewTypes: BLOB_WITH_PREVIEW_FILE_2.previewFile!.availablePreviewTypes,
  },
  pdfPreviewFileJson: null,
  uploadStatus: BLOB_WITH_PREVIEW_FILE_2.uploadStatus!,
};

export const BLOB_WITH_PDF_PREVIEW_FILE_1: BenchlingBlob = {
  id: '751aac49-1e6f-4905-9eea-60906832b9e2',
  name: 'image2.png',
  link_type: 'result_blob',
  url: '/results/blobs/751aac49-1e6f-4905-9eea-60906832b9e2/s3-url',
  previewFile: null,
  pdfPreviewFile: {
    id: 'efl_ksdjfsdf09890',
    previewType: EXTERNAL_FILE_TYPE.PDF,
    url: 'https://www.benchling.com/wp-content/uploads/2020/05/Benchling-Series-D-HeaderAsset-2.png',
    availablePreviewTypes: ['pdf'],
  },
  uploadStatus: BlobUploadStatus.COMPLETE,
};

export const BLOB_WITH_PDF_PREVIEW_FILE_1_GQL: ResultBlobGraphQL = {
  __typename: 'ResultBlobNode',
  id: BLOB_WITH_PDF_PREVIEW_FILE_1.id,
  name: BLOB_WITH_PDF_PREVIEW_FILE_1.name,
  url: BLOB_WITH_PDF_PREVIEW_FILE_1.url,
  previewFileJson: null,
  pdfPreviewFileJson: {
    id: BLOB_WITH_PDF_PREVIEW_FILE_1.pdfPreviewFile!.id,
    previewType: BLOB_WITH_PDF_PREVIEW_FILE_1.pdfPreviewFile!.previewType,
    url: BLOB_WITH_PDF_PREVIEW_FILE_1.pdfPreviewFile!.url,
    availablePreviewTypes: BLOB_WITH_PDF_PREVIEW_FILE_1.pdfPreviewFile!.availablePreviewTypes,
  },
  uploadStatus: BLOB_WITH_PDF_PREVIEW_FILE_1.uploadStatus!,
};

export const BLOB_WITH_BOTH_PREVIEW_FILES: BenchlingBlob = {
  ...BLOB_WITH_PREVIEW_FILE_1,
  pdfPreviewFile: {
    id: 'efl_ksdjfsdf09890',
    previewType: EXTERNAL_FILE_TYPE.PDF,
    url: 'https://www.benchling.com/wp-content/uploads/2020/05/Benchling-Series-D-HeaderAsset-2.png',
    availablePreviewTypes: ['pdf'],
  },
  uploadStatus: BlobUploadStatus.COMPLETE,
};

export const BLOB_WITH_BOTH_PREVIEW_FILES_GQL: ResultBlobGraphQL = {
  ...BLOB_WITH_PREVIEW_FILE_1_GQL,
  pdfPreviewFileJson: BLOB_WITH_BOTH_PREVIEW_FILES.pdfPreviewFile,
  uploadStatus: BLOB_WITH_BOTH_PREVIEW_FILES.uploadStatus!,
};

export const WAREHOUSE_BLOB: WarehouseBlob = {
  id: '8ea7dd7a-4242-a7c4-8d88-164728562755',
  name: 'image1.png',
  url: '/results/blobs/8ea7dd7a-4242-a7c4-8d88-164728562755/s3-url',
};

export const SCHEMA_TAG_BLOB_LINK = {
  name: 'blob_link',
  tagSchemaField: SCHEMA_FIELD_BLOB_LINK,
  targetBlobs: [BLOB_WITH_PREVIEW_FILE_1, BLOB_WITH_PREVIEW_FILE_2],
  value: 'image1.png, image2.png',
};

export const RECIPE_SCHEMA_CONFIG = {
  allowMeasuredIngredients: false,
  componentLotStorageEnabled: null,
  componentLotTextEnabled: null,
};

const RECIPE_SCHEMA_CONFIG_GQL: TagSchemaListingWithFieldsGraphQL['mixtureSchemaConfig'] = {
  __typename: 'MixtureSchemaConfigurationNode',
  ...RECIPE_SCHEMA_CONFIG,
};

export const PREP_SCHEMA_CONFIG = {
  allowMeasuredIngredients: true,
  componentLotTextEnabled: true,
  componentLotStorageEnabled: false,
};

const PREP_SCHEMA_CONFIG_GQL: TagSchemaListingWithFieldsGraphQL['mixtureSchemaConfig'] = {
  __typename: 'MixtureSchemaConfigurationNode',
  ...PREP_SCHEMA_CONFIG,
};

export const MIXTURE_RECIPE_TAG_SCHEMA: TagSchema = {
  ...BFI_TAG_SCHEMA,
  mixtureSchemaConfig: RECIPE_SCHEMA_CONFIG,
  folderItemType: ModelTypeConstants.MIXTURE,
  id: 'ts_Rp9WDsPQ',
  name: 'Media Recipe',
  sqlIdentifier: 'media_recipe',
  iconId: FolderItemIcon.Mixture,
  prefix: 'MR',
};

export const MIXTURE_RECIPE_TAG_SCHEMA_GQL: TagSchemaGraphQL = {
  ...BFI_TAG_SCHEMA_GQL,
  id: MIXTURE_RECIPE_TAG_SCHEMA.id,
  name: MIXTURE_RECIPE_TAG_SCHEMA.name,
  iconId: MIXTURE_RECIPE_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.mixture,
  sequenceType: MIXTURE_RECIPE_TAG_SCHEMA.sequenceType,
  sqlIdentifier: MIXTURE_RECIPE_TAG_SCHEMA.sqlIdentifier,
  prefix: MIXTURE_RECIPE_TAG_SCHEMA.prefix,
  shouldCreateAsOligo: false,
  mixtureSchemaConfig: RECIPE_SCHEMA_CONFIG_GQL,
};

export const MIXTURE_RECIPE_TAG_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  ...BFI_TAG_SCHEMA_SUMMARY_GQL,
  id: MIXTURE_RECIPE_TAG_SCHEMA.id,
  name: MIXTURE_RECIPE_TAG_SCHEMA.name,
  iconId: MIXTURE_RECIPE_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.mixture,
  sequenceType: MIXTURE_RECIPE_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  sqlIdentifier: MIXTURE_RECIPE_TAG_SCHEMA.sqlIdentifier,
  archiveRecord: null,
};

export const MIXTURE_RECIPE_TAG_SCHEMA_LISTING_GQL: TagSchemaListingGraphQL = {
  __typename: 'TagSchemaNode',
  id: MIXTURE_RECIPE_TAG_SCHEMA.id,
  name: MIXTURE_RECIPE_TAG_SCHEMA.name,
  iconId: MIXTURE_RECIPE_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.mixture,
  sequenceType: MIXTURE_RECIPE_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  permissions: TAG_SCHEMA_PERMISSIONS_GQL,
};

export const ENTRY_SCHEMA_WITH_FIELDS: TagSchema = {
  ...ENTRY_SCHEMA,
  fields: [SCHEMA_FIELD_DATE, SCHEMA_FIELD_TEXT, SCHEMA_FIELD_INTEGER],
  allFields: [SCHEMA_FIELD_DATE, SCHEMA_FIELD_TEXT, SCHEMA_FIELD_INTEGER],
};

export const TAG_SCHEMA_WITH_STATIC_SCHEMA_INTERFACE_LINK: TagSchema = {
  ...TAG_SCHEMA,
  fields: [SCHEMA_FIELD_STATIC_SCHEMA_INTERFACE_LINK],
};

export const ENTRY_SCHEMA_WITH_FIELDS_GQL: TagSchemaGraphQL = restToGraphQL(
  'TagSchema',
  ENTRY_SCHEMA_WITH_FIELDS
);

export const MOLECULE_TAG_SCHEMA: TagSchema = {
  ...BFI_TAG_SCHEMA,
  folderItemType: ModelTypeConstants.MOLECULE,
  id: 'ts_apw8DsCQ',
  name: 'Molecule schema',
  sqlIdentifier: 'molecule_schema',
  iconId: FolderItemIcon.Molecule,
  prefix: 'MOL',
};

export const MOLECULE_TAG_SCHEMA_GQL = restToGraphQL('TagSchema', MOLECULE_TAG_SCHEMA);

export const MOLECULE_TAG_SCHEMA_LISTING_GQL: TagSchemaListingGraphQL = {
  __typename: 'TagSchemaNode',
  id: MOLECULE_TAG_SCHEMA.id,
  name: MOLECULE_TAG_SCHEMA.name,
  iconId: MOLECULE_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.molecule,
  sequenceType: MOLECULE_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  permissions: TAG_SCHEMA_PERMISSIONS_GQL,
};

export const MOLECULE_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  ...MOLECULE_TAG_SCHEMA_LISTING_GQL,
  allFields: MOLECULE_TAG_SCHEMA.allFields,
  fields: MOLECULE_TAG_SCHEMA.fields,
};

export const MOLECULE_TAG_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  id: MOLECULE_TAG_SCHEMA.id,
  name: MOLECULE_TAG_SCHEMA.name,
  iconId: MOLECULE_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.molecule,
  sequenceType: MOLECULE_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  sqlIdentifier: MOLECULE_TAG_SCHEMA.sqlIdentifier,
  archiveRecord: null,
};

export const PARENT_LINK_MIXTURE_FIELD: SchemaField = {
  ...SCHEMA_FIELD_BIOENTITY_LINK,
  isParentLink: true,
  requiredLink: {
    tagSchema: MIXTURE_RECIPE_TAG_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
};

export const MIXTURE_PREP_TAG_SCHEMA: TagSchema = {
  ...BFI_TAG_SCHEMA,
  folderItemType: ModelTypeConstants.MIXTURE,
  mixtureSchemaConfig: PREP_SCHEMA_CONFIG,
  isContainable: true,
  id: 'ts_PREP1234',
  name: 'Media Prep',
  sqlIdentifier: 'media_prep',
  iconId: FolderItemIcon.Mixture,
  prefix: 'MP',
  allFields: [PARENT_LINK_MIXTURE_FIELD],
  fields: [PARENT_LINK_MIXTURE_FIELD],
};

export const MIXTURE_PREP_TAG_SCHEMA_GQL: TagSchemaGraphQL = {
  ...BFI_TAG_SCHEMA_GQL,
  id: MIXTURE_PREP_TAG_SCHEMA.id,
  name: MIXTURE_PREP_TAG_SCHEMA.name,
  iconId: MIXTURE_PREP_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.mixture,
  sequenceType: MIXTURE_PREP_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  isContainable: MIXTURE_PREP_TAG_SCHEMA.isContainable,
  mixtureSchemaConfig: PREP_SCHEMA_CONFIG_GQL,
  prefix: MIXTURE_PREP_TAG_SCHEMA.prefix,
  sqlIdentifier: MIXTURE_PREP_TAG_SCHEMA.sqlIdentifier,
  allFieldsJson: MIXTURE_PREP_TAG_SCHEMA.allFields,
  fieldsJson: MIXTURE_PREP_TAG_SCHEMA.fields,
};

export const MIXTURE_PREP_TAG_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_GQL,
  id: MIXTURE_PREP_TAG_SCHEMA.id,
  name: MIXTURE_PREP_TAG_SCHEMA.name,
  iconId: MIXTURE_PREP_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.mixture,
  sequenceType: MIXTURE_PREP_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  sqlIdentifier: MIXTURE_PREP_TAG_SCHEMA.sqlIdentifier,
  archiveRecord: null,
};

export const MIXTURE_PREP_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: MIXTURE_PREP_TAG_SCHEMA.id,
  name: MIXTURE_PREP_TAG_SCHEMA.name,
  iconId: MIXTURE_PREP_TAG_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.mixture,
  sequenceType: MIXTURE_PREP_TAG_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  isContainable: MIXTURE_PREP_TAG_SCHEMA.isContainable,
  allFields: MIXTURE_PREP_TAG_SCHEMA.allFields,
  fields: MIXTURE_PREP_TAG_SCHEMA.fields,
  mixtureSchemaConfig: PREP_SCHEMA_CONFIG_GQL,
};

export const PARENTLESS_MIXTURE_PREP_TAG_SCHEMA: TagSchema = {
  ...MIXTURE_PREP_TAG_SCHEMA,
  id: 'ts_PREP5678',
  name: 'Parentless Media Prep',
  allFields: [],
  fields: [],
};

export const PARENTLESS_MIXTURE_PREP_TAG_SCHEMA_GQL: TagSchemaGraphQL = {
  ...MIXTURE_PREP_TAG_SCHEMA_GQL,
  id: PARENTLESS_MIXTURE_PREP_TAG_SCHEMA.id,
  name: PARENTLESS_MIXTURE_PREP_TAG_SCHEMA.name,
  allFieldsJson: PARENTLESS_MIXTURE_PREP_TAG_SCHEMA.allFields,
  fieldsJson: PARENTLESS_MIXTURE_PREP_TAG_SCHEMA.fields,
};

export const PARENTLESS_MIXTURE_PREP_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...MIXTURE_PREP_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: PARENTLESS_MIXTURE_PREP_TAG_SCHEMA.id,
  name: PARENTLESS_MIXTURE_PREP_TAG_SCHEMA.name,
};

export const MIXTURE_TARGET_FILE: TargetFile = {
  url: '/testorg/f/PBDXljD0-enterprise-registry/mxt-7HT3KyPx-mixture0/edit',
  id: 'mxt_7HT3KyPx',
  name: 'mixture0',
};

export const SELECTOR_OPTION_1 = {id: 'sfso_1', name: 'option 1', archiveRecord: null};
export const SELECTOR_OPTION_1_GQL: SchemaFieldSelectorOptionGraphQL = {
  ...pickDefined(SELECTOR_OPTION_1, 'id', 'name'),
  __typename: 'SchemaFieldSelectorOptionNode',
  archiveRecord: null,
};
export const SELECTOR_OPTION_2 = {id: 'sfso_2', name: 'option 2', archiveRecord: null};
export const SELECTOR_OPTION_2_GQL: SchemaFieldSelectorOptionGraphQL = {
  ...pickDefined(SELECTOR_OPTION_2, 'id', 'name'),
  __typename: 'SchemaFieldSelectorOptionNode',
  archiveRecord: null,
};
export const SELECTOR_OPTION_3 = {id: 'sfso_3', name: 'option 3', archiveRecord: null};
export const SELECTOR_OPTION_3_GQL: SchemaFieldSelectorOptionGraphQL = {
  ...pickDefined(SELECTOR_OPTION_3, 'id', 'name'),
  __typename: 'SchemaFieldSelectorOptionNode',
  archiveRecord: null,
};
export const SELECTOR_OPTION_4 = {id: 'sfso_4', name: 'option 4', archiveRecord: null};
export const SELECTOR_OPTION_4_GQL: SchemaFieldSelectorOptionGraphQL = {
  ...pickDefined(SELECTOR_OPTION_4, 'id', 'name'),
  __typename: 'SchemaFieldSelectorOptionNode',
  archiveRecord: null,
};

export const ARCHIVED_SELECTOR_OPTION = {
  id: 'sfso_5',
  name: 'option 5',
  archiveRecord: ARCHIVE_RECORD,
};

export const ARCHIVED_SELECTOR_OPTION_GQL: SchemaFieldSelectorOptionGraphQL = {
  ...pickDefined(ARCHIVED_SELECTOR_OPTION, 'id', 'name'),
  __typename: 'SchemaFieldSelectorOptionNode',
  archiveRecord: ARCHIVE_RECORD_GQL,
};

export const SELECTOR_1 = {
  id: 'sfs_r1dU5DTZ',
  archiveRecord: null,
  name: 'selector 1',
  schemaFieldSelectorOptions: [SELECTOR_OPTION_1, SELECTOR_OPTION_2],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_1, SELECTOR_OPTION_2],
};
export const SELECTOR_1_GQL: SchemaFieldSelectorGraphQL = {
  ...pickDefined(SELECTOR_1, 'id', 'name'),
  __typename: 'SchemaFieldSelectorNode',
  archiveRecord: null,
  schemaFieldSelectorOptions: [SELECTOR_OPTION_1_GQL, SELECTOR_OPTION_2_GQL],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_1_GQL, SELECTOR_OPTION_2_GQL],
};

export const SELECTOR_2: SchemaFieldSelector = {
  id: 'sfs_hVbC86OO',
  archiveRecord: null,
  name: 'selector 2',
  schemaFieldSelectorOptions: [SELECTOR_OPTION_3, SELECTOR_OPTION_4],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_3, SELECTOR_OPTION_4],
};
export const SELECTOR_2_GQL: SchemaFieldSelectorGraphQL = {
  ...pickDefined(SELECTOR_2, 'id', 'name'),
  __typename: 'SchemaFieldSelectorNode',
  archiveRecord: null,
  schemaFieldSelectorOptions: [SELECTOR_OPTION_3_GQL, SELECTOR_OPTION_4_GQL],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_3_GQL, SELECTOR_OPTION_4_GQL],
};

export const SELECTORS = [SELECTOR_1, SELECTOR_2];
export const SELECTORS_GQL = [SELECTOR_1_GQL, SELECTOR_2_GQL];

export const ARCHIVED_SELECTOR = {
  id: 'sfs_r1dUwe2Z',
  archiveRecord: ARCHIVE_RECORD,
  name: 'archived selector',
  schemaFieldSelectorOptions: [SELECTOR_OPTION_1, SELECTOR_OPTION_2],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_1, SELECTOR_OPTION_2],
};

export const ARCHIVED_SELECTOR_GQL: SchemaFieldSelectorGraphQL = {
  ...pickDefined(ARCHIVED_SELECTOR, 'id', 'name'),
  __typename: 'SchemaFieldSelectorNode',
  archiveRecord: ARCHIVE_RECORD_GQL,
  schemaFieldSelectorOptions: [SELECTOR_OPTION_1_GQL, SELECTOR_OPTION_2_GQL],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_1_GQL, SELECTOR_OPTION_2_GQL],
};

export const SELECTOR_WITH_ARCHIVED_OPTION = {
  id: 'sfs_r1dU5DTZ',
  archiveRecord: null,
  name: 'selector 1',
  schemaFieldSelectorOptions: [SELECTOR_OPTION_1],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_1, ARCHIVED_SELECTOR_OPTION],
};

export const SELECTOR_WITH_ARCHIVED_OPTION_GQL: SchemaFieldSelectorGraphQL = {
  __typename: 'SchemaFieldSelectorNode',
  ...pickDefined(SELECTOR_WITH_ARCHIVED_OPTION, 'id', 'name'),
  schemaFieldSelectorOptions: [SELECTOR_OPTION_1_GQL],
  allSchemaFieldSelectorOptions: [SELECTOR_OPTION_1_GQL, ARCHIVED_SELECTOR_OPTION_GQL],
  archiveRecord: null,
};

export const SELECTOR_WITH_SEVERAL_OPTIONS = {
  id: 'sfs_r1dU5DTZ',
  archiveRecord: null,
  name: 'selector 1',
  schemaFieldSelectorOptions: [SELECTOR_OPTION_1, SELECTOR_OPTION_2, SELECTOR_OPTION_3],
  allSchemaFieldSelectorOptions: [
    ARCHIVED_SELECTOR_OPTION,
    SELECTOR_OPTION_1,
    SELECTOR_OPTION_2,
    SELECTOR_OPTION_3,
  ],
};

export const INACCESSIBLE_SELECTOR = {
  __typename: 'InaccessibleInfoNode',
  invalidId: SELECTOR_1_GQL.id,
  canRequestAccess: false,
};

export const SCHEMA_FIELD_SELECTOR: SchemaField = {
  id: 32,
  apiId: 'tsf_sFls0lem',
  dbId: 32,
  fieldType: FieldType.SELECTOR,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'Resistance Gene',
  displayName: 'Resistance Gene',
  systemName: 'resistance_gene',
  requiredLink: null,
  schemaFieldSelectorId: SELECTOR_2.id,
  archiveRecord: null,
};

export const SCHEMA_FIELD_SELECTOR_2 = {
  ...SCHEMA_FIELD_SELECTOR,
  id: 42,
  apiId: 'tsf_sFls0len',
  dbId: 42,
  fieldType: FieldType.SELECTOR,
  name: 'Other Resistance Gene',
  displayName: 'Other Resistance Gene',
  systemName: 'other_resistance_gene',
  isMulti: false,
  schemaFieldSelectorId: SELECTOR_2.id,
};

export const SCHEMA_TAG_SELECTOR = {
  tagSchemaField: SCHEMA_FIELD_SELECTOR,
  schemaFieldSelectorOptionIds: ['sfso_1'],
  value: 'option 1',
  name: 'Resistance Gene',
};

export const SCHEMA_FIELD_FLOAT_WITH_LEGAL_TEXT: SchemaField = {
  id: 34,
  apiId: 'tsf_NvMrc3y7',
  dbId: 34,
  fieldType: FieldType.FLOAT,
  name: 'Temperature',
  displayName: 'Temperature',
  systemName: 'temperature',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  legalTextSelectorId: SELECTOR_2.id,
  archiveRecord: null,
};

export const SCHEMA_FIELD_COMPUTED: ComputedSchemaField = {
  id: 33,
  apiId: 'tsf_c0Mpwkst',
  dbId: 33,
  fieldType: FieldType.FLOAT,
  isRequired: false,
  isComputed: true,
  isConvertedFromLink: false,
  isIntegration: false,
  isSnapshot: false,
  isParentLink: false,
  isMulti: false,
  name: 'Molecular Weight',
  displayName: 'Molecular Weight',
  systemName: 'molecular_weight',
  requiredLink: null,
  schemaFieldSelectorId: null,
  dependencies: [LITERAL_COMPUTED_FIELD_DEPENDENCY],
  formulaName: 'identity_fn',
  archiveRecord: null,
};

export const SCHEMA_FIELD_INTEGRATION: SchemaField = {
  id: 38,
  dbId: 38,
  apiId: 'tsf_321',
  fieldType: FieldType.TEXT,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: true,
  isParentLink: false,
  isMulti: false,
  name: 'Cat',
  displayName: 'Cat',
  systemName: 'cat',
  requiredLink: null,
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_INTEGRATION: SchemaTag = {
  name: 'IntegrationTag',
  tagSchemaField: SCHEMA_FIELD_INTEGRATION,
  url: '',
  value: '',
};

export const SCHEMA_FIELD_GENERIC_SEQUENCE_LINK: SchemaField = {
  ...SCHEMA_FIELD_BIOENTITY_LINK,
  name: 'Important sequences',
  apiId: 'tsf_456def',
  requiredLink: {
    tagSchema: null,
    folderItemType: 'sequence',
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
};

export const SCHEMA_TAG_COMPUTED_SUCCEEDED: ComputedSchemaTag = {
  tagSchemaField: SCHEMA_FIELD_COMPUTED,
  value: '0.5',
  rawValue: '0.5',
  floatValue: 0.5,
  name: 'Molecular Weight',
  computedState: {
    status: ComputedFieldStatus.SUCCEEDED,
    errorMessage: null,
  },
};

export const SCHEMA_TAG_COMPUTED_PENDING: ComputedSchemaTag = {
  tagSchemaField: SCHEMA_FIELD_COMPUTED,
  value: '',
  name: 'Molecular Weight',
  computedState: {
    status: ComputedFieldStatus.PENDING,
    errorMessage: null,
  },
};

export const SCHEMA_TAG_COMPUTED_FAILED: ComputedSchemaTag = {
  tagSchemaField: SCHEMA_FIELD_COMPUTED,
  value: '',
  name: 'Molecular Weight',
  computedState: {
    status: ComputedFieldStatus.FAILED,
    errorMessage: 'Could not compute',
  },
};

export const CAT_TAG: SchemaTag = {
  name: 'Cat',
  tagSchemaField: SCHEMA_FIELD_TEXT,
  value: 'Tabby',
};

export const TAG_SCHEMA_2_GQL = {
  ...TAG_SCHEMA_GQL,
  id: 'ts_BbwxP6Js',
  name: 'Plasmid 2',
};

export const TAG_SCHEMA_WITH_CHILD_ENTITIES: TagSchema = {
  ...TAG_SCHEMA,
  batchSchemaId: 'batsch_12345',
  childEntitySchemaSummaries: [TAG_SCHEMA_SUMMARY],
};

export const TAG_SCHEMA_WITH_CHILD_ENTITIES_GQL: TagSchemaGraphQL = {
  __typename: 'TagSchemaNode',
  allFieldsJson: [SCHEMA_FIELD_TEXT],
  archiveRecord: null,
  authParentOption: TAG_SCHEMA_WITH_CHILD_ENTITIES.authParentOption,
  batchSchema: {
    __typename: 'BatchSchemaNode',
    id: TAG_SCHEMA_WITH_CHILD_ENTITIES.batchSchemaId!,
  },
  childEntitySchemas: [
    {
      // eslint-disable-next-line no-restricted-syntax
      ..._.omit(TAG_SCHEMA_SUMMARY, 'archiveRecord'),
      __typename: 'TagSchemaNode',
      archiveRecord: null,
    },
  ],
  constraint: null,
  containableType: TAG_SCHEMA_WITH_CHILD_ENTITIES.containableType,
  permissions: SCHEMA_PERMISSIONS_GQL,
  fieldsJson: TAG_SCHEMA_WITH_CHILD_ENTITIES.fields,
  folderItemType: TagSchemaSupportedFolderItemType.sequence,
  hoistedTagSchemaGraphsJson: TAG_SCHEMA_WITH_CHILD_ENTITIES.hoistedTagSchemaGraphs,
  iconId: TAG_SCHEMA_WITH_CHILD_ENTITIES.iconId,
  id: TAG_SCHEMA_WITH_CHILD_ENTITIES.id,
  includeRegistryIdInChips: TAG_SCHEMA_WITH_CHILD_ENTITIES.includeRegistryIdInChips,
  isContainable: TAG_SCHEMA_WITH_CHILD_ENTITIES.isContainable,
  labelingStrategies: TAG_SCHEMA_WITH_CHILD_ENTITIES.labelingStrategies,
  mixtureSchemaConfig: null,
  name: TAG_SCHEMA_WITH_CHILD_ENTITIES.name,
  nameTemplateParts: [],
  prefix: TAG_SCHEMA_WITH_CHILD_ENTITIES.prefix,
  registry: {
    __typename: 'RegistryNode',
    id: TAG_SCHEMA_WITH_CHILD_ENTITIES.registryId,
  },
  sequenceType: TAG_SCHEMA_WITH_CHILD_ENTITIES.sequenceType,
  shouldCreateAsOligo: TAG_SCHEMA_WITH_CHILD_ENTITIES.shouldCreateAsOligo,
  shouldOrderNamePartsBySequence: TAG_SCHEMA_WITH_CHILD_ENTITIES.shouldOrderNamePartsBySequence,
  showResidues: TAG_SCHEMA_WITH_CHILD_ENTITIES.showResidues,
  sqlIdentifier: TAG_SCHEMA_WITH_CHILD_ENTITIES.sqlIdentifier,
  useOrganizationCollectionAliasForDisplayLabel:
    TAG_SCHEMA_WITH_CHILD_ENTITIES.useOrganizationCollectionAliasForDisplayLabel,
  useRandomOrgAlias: TAG_SCHEMA_WITH_CHILD_ENTITIES.useRandomOrgAlias,
  schemaDescription: TAG_SCHEMA_WITH_CHILD_ENTITIES.schemaDescription,
};

export const TAG_SCHEMA_WITH_CHILD_ENTITIES_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  childEntitySchemas: [TAG_SCHEMA_SUMMARY_GQL],
};

export const SEQUENCE_SCHEMA = {...TAG_SCHEMA};

export const SEQUENCE_SCHEMA_GQL: TagSchemaGraphQL = {
  ...TAG_SCHEMA_GQL,
  id: SEQUENCE_SCHEMA.id,
  name: SEQUENCE_SCHEMA.name,
  sequenceType: SEQUENCE_SCHEMA.sequenceType,
  sqlIdentifier: SEQUENCE_SCHEMA.sqlIdentifier,
};

export const SEQUENCE_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  id: SEQUENCE_SCHEMA.id,
  name: SEQUENCE_SCHEMA.name,
  iconId: SEQUENCE_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.sequence,
  sequenceType: SEQUENCE_SCHEMA.sequenceType,
  shouldCreateAsOligo: SEQUENCE_SCHEMA.shouldCreateAsOligo,
  sqlIdentifier: SEQUENCE_SCHEMA.sqlIdentifier,
  archiveRecord: null,
};

export const SEQUENCE_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
};

export const SEQUENCE_SCHEMA_CHIP_GQL: TagSchemaChipGraphQL = {
  __typename: 'TagSchemaNode',
  id: SEQUENCE_SCHEMA.id,
  name: SEQUENCE_SCHEMA.name,
  iconId: SEQUENCE_SCHEMA.iconId,
  archiveRecord: null,
};

export const MIXED_SCHEMA: TagSchema = {
  ...SEQUENCE_SCHEMA,
  id: 'ts_mIxEd',
  sequenceType: SequenceSchemaType.MIXED,
};

export const MIXED_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  ...SEQUENCE_SCHEMA_SUMMARY_GQL,
  id: MIXED_SCHEMA.id,
  sequenceType: MIXED_SCHEMA.sequenceType,
};

export const MIXED_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...SEQUENCE_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: MIXED_SCHEMA.id,
  sequenceType: MIXED_SCHEMA.sequenceType,
};

export const RNA_SEQUENCE_SCHEMA: TagSchema = {
  ...SEQUENCE_SCHEMA,
  name: 'mRNA',
  id: 'ts_mRnA',
  sequenceType: SequenceSchemaType.RNA_SEQUENCE,
  sqlIdentifier: 'mrna',
};

export const RNA_SEQUENCE_SCHEMA_GQL: TagSchemaGraphQL = {
  ...SEQUENCE_SCHEMA_GQL,
  id: RNA_SEQUENCE_SCHEMA.id,
  name: RNA_SEQUENCE_SCHEMA.name,
  sequenceType: RNA_SEQUENCE_SCHEMA.sequenceType,
  sqlIdentifier: RNA_SEQUENCE_SCHEMA.sqlIdentifier,
};

export const RNA_SEQUENCE_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  ...SEQUENCE_SCHEMA_SUMMARY_GQL,
  id: RNA_SEQUENCE_SCHEMA.id,
  name: RNA_SEQUENCE_SCHEMA.name,
  sequenceType: RNA_SEQUENCE_SCHEMA.sequenceType,
  sqlIdentifier: RNA_SEQUENCE_SCHEMA.sqlIdentifier,
};

export const RNA_SEQUENCE_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...SEQUENCE_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: RNA_SEQUENCE_SCHEMA.id,
  name: RNA_SEQUENCE_SCHEMA.name,
  sequenceType: RNA_SEQUENCE_SCHEMA.sequenceType,
  sqlIdentifier: RNA_SEQUENCE_SCHEMA.sqlIdentifier,
};

export const RNA_SEQUENCE_SCHEMA_CHIP_GQL: TagSchemaChipGraphQL = {
  __typename: 'TagSchemaNode',
  id: RNA_SEQUENCE_SCHEMA.id,
  name: RNA_SEQUENCE_SCHEMA.name,
  iconId: RNA_SEQUENCE_SCHEMA.iconId,
  archiveRecord: null,
};

export const DNA_OLIGO_SCHEMA: TagSchema = {
  ...SEQUENCE_SCHEMA,
  name: 'Primer',
  id: 'ts_oLiGO',
  sequenceType: SequenceSchemaType.OLIGO,
  sqlIdentifier: 'primer',
};

export const DNA_OLIGO_SCHEMA_GQL: TagSchemaGraphQL = {
  ...SEQUENCE_SCHEMA_GQL,
  id: DNA_OLIGO_SCHEMA.id,
  name: DNA_OLIGO_SCHEMA.name,
  sequenceType: DNA_OLIGO_SCHEMA.sequenceType,
  sqlIdentifier: DNA_OLIGO_SCHEMA.sqlIdentifier,
};

export const DNA_OLIGO_SCHEMA_SUMMARY_GQL: TagSchemaSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  id: DNA_OLIGO_SCHEMA_GQL.id,
  name: DNA_OLIGO_SCHEMA_GQL.name,
  sequenceType: DNA_OLIGO_SCHEMA_GQL.sequenceType,
  sqlIdentifier: DNA_OLIGO_SCHEMA_GQL.sqlIdentifier,
  iconId: DNA_OLIGO_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.sequence,
  shouldCreateAsOligo: DNA_OLIGO_SCHEMA_GQL.shouldCreateAsOligo,
  archiveRecord: null,
};

export const DNA_OLIGO_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...SEQUENCE_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: DNA_OLIGO_SCHEMA_GQL.id,
  name: DNA_OLIGO_SCHEMA_GQL.name,
  sequenceType: DNA_OLIGO_SCHEMA_GQL.sequenceType,
  sqlIdentifier: DNA_OLIGO_SCHEMA_GQL.sqlIdentifier,
};

export const DNA_OLIGO_SCHEMA_CHIP_GQL: TagSchemaChipGraphQL = {
  __typename: 'TagSchemaNode',
  id: DNA_OLIGO_SCHEMA.id,
  name: DNA_OLIGO_SCHEMA.name,
  iconId: DNA_OLIGO_SCHEMA.iconId,
  archiveRecord: null,
};

export const RNA_OLIGO_SCHEMA: TagSchema = {
  ...SEQUENCE_SCHEMA,
  name: 'CRISPR Guide',
  id: 'ts_rNaol',
  sqlIdentifier: 'crispr_guide',
  sequenceType: SequenceSchemaType.RNA_OLIGO,
};

export const RNA_OLIGO_SCHEMA_GQL = restToGraphQL('TagSchema', RNA_OLIGO_SCHEMA);

export const RNA_OLIGO_SCHEMA_LISTING_GQL: TagSchemaListingGraphQL = {
  __typename: 'TagSchemaNode',
  id: RNA_OLIGO_SCHEMA.id,
  name: RNA_OLIGO_SCHEMA.name,
  iconId: RNA_OLIGO_SCHEMA.iconId,
  folderItemType: TagSchemaSupportedFolderItemType.sequence,
  sequenceType: RNA_OLIGO_SCHEMA.sequenceType,
  shouldCreateAsOligo: false,
  permissions: TAG_SCHEMA_PERMISSIONS_GQL,
};

export const RNA_OLIGO_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...SEQUENCE_SCHEMA_LISTING_WITH_FIELDS_GQL,
  ...RNA_OLIGO_SCHEMA_LISTING_GQL,
};

export const SCHEMA_FIELD_DNA_OLIGO_LINK: SchemaField = {
  ...SCHEMA_FIELD_BIOENTITY_LINK,
  name: 'Oligos',
  apiId: 'tsf_abc123',
  requiredLink: {
    tagSchema: DNA_OLIGO_SCHEMA,
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
  },
};

export const SCHEMA_FIELD_RNA_OLIGO_LINK: SchemaField = {
  ...SCHEMA_FIELD_BIOENTITY_LINK,
  name: 'Oligos',
  apiId: 'tsf_abc123',
  requiredLink: {
    tagSchema: RNA_OLIGO_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
};

export const SCHEMA_FIELD_MIXED_SEQUENCE_LINK: SchemaField = {
  ...SCHEMA_FIELD_BIOENTITY_LINK,
  name: 'Oligos and Sequences',
  apiId: 'tsf_jlsdjk23',
  requiredLink: {
    tagSchema: MIXED_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
};

export const CREATE_AS_OLIGO_SCHEMA: TagSchema = {
  ...SEQUENCE_SCHEMA,
  name: 'Primer',
  id: 'ts_oLiGo',
  sequenceType: SequenceSchemaType.MIXED,
  shouldCreateAsOligo: true,
};

export const CREATE_AS_OLIGO_SCHEMA_GQL = restToGraphQL('TagSchema', CREATE_AS_OLIGO_SCHEMA);

export const CREATE_AS_OLIGO_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...SEQUENCE_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: CREATE_AS_OLIGO_SCHEMA.id,
  name: CREATE_AS_OLIGO_SCHEMA.name,
  sequenceType: CREATE_AS_OLIGO_SCHEMA.sequenceType,
  shouldCreateAsOligo: CREATE_AS_OLIGO_SCHEMA.shouldCreateAsOligo,
};

export const TAG_SCHEMA_WITH_NAME_PARTS: TagSchema = {
  allFields: [SCHEMA_FIELD_TEXT_2],
  archiveRecord: null,
  authParentOption: AuthParentOption.FOLDER,
  batchSchemaId: null,
  childEntitySchemaSummaries: [],
  constraint: null,
  containableType: TagSchemaContainableType.NONE,
  fields: [SCHEMA_FIELD_TEXT_2],
  folderItemType: ModelTypeConstants.SEQUENCE,
  hoistedTagSchemaGraphs: [],
  iconId: FolderItemIcon.Sequence,
  id: 'ts_wiej03SJ',
  includeRegistryIdInChips: false,
  isContainable: false,
  name: 'Primer',
  labelingStrategies: [RegistryLabelingStrategy.SET_FROM_NAME_PARTS],
  mixtureSchemaConfig: null,
  nameTemplateParts: [
    {
      type: NameTemplatePartType.FIELD,
      displayName: null,
      exampleValue: null,
      fieldId: SCHEMA_FIELD_TEXT_2.apiId,
      text: null,
      // @ts-expect-error
      datetimeFormat: null,
      typeSpecificConfiguration: null,
    },
  ],
  prefix: 'pr',
  registryId: REGISTRY_SUMMARY.id,
  resultSchema: null,
  sequenceType: SequenceSchemaType.OLIGO,
  showResidues: false,
  shouldCreateAsOligo: false,
  shouldOrderNamePartsBySequence: false,
  sqlIdentifier: 'primer',
  useRandomOrgAlias: false,
  useOrganizationCollectionAliasForDisplayLabel: false,
  permissions: TAG_SCHEMA_PERMISSIONS,
  schemaDescription: '',
};

export const CUSTOM_TAG: CustomTag = {
  name: 'custom',
  value: 'value',
  display: 'custom:value',
  sortable: true,
};

export const SCHEMA_FIELD_ENTRY_LINK: SchemaField = {
  id: 54,
  apiId: 'tsf_001_entry_link',
  dbId: 54,
  fieldType: FieldType.ENTRY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  name: 'Entry',
  displayName: 'Entry',
  systemName: 'entry',
  requiredLink: {
    tagSchema: TAG_SCHEMA,
    folderItemType: null,
    entitySchemaFieldPath: null,
    schemaInterface: null,
  },
  schemaFieldSelectorId: null,
  archiveRecord: null,
};

export const SCHEMA_TAG_ENTRY_LINK = {
  name: 'Entry',
  tagSchemaField: SCHEMA_FIELD_ENTRY_LINK,
  targetEntries: [
    {
      url: '/testorg/f/PBDXljD0-enterprise-registry/an-entry/edit',
      id: 'etr_Asj2NBdk',
      name: 'An entry',
      humanId: null,
    },
  ],
  url: '',
  value: '',
};

export const SCHEMA_FIELD_ASSAY_RUN_LINK: AssayLinkSchemaField = {
  apiId: 'tsf_dgE45nbs',
  archiveRecord: null,
  dbId: 83,
  displayName: 'flow_run',
  fieldType: FieldType.ASSAY_RUN_LINK,
  id: 83,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  isRequired: false,
  name: 'flow_run',
  numericMax: null,
  numericMin: null,
  requiredLink: {
    assaySchema: ASSAY_SCHEMAS_BY_ID.assaysch_1,
  },
  schemaFieldSelectorId: null,
  systemName: 'flow_run',
};

export const SCHEMA_TAG_ASSAY_RUN_LINK: SchemaTag = {
  tagSchemaField: SCHEMA_FIELD_ASSAY_RUN_LINK,
  computedState: null,
  name: 'flow_run',
  value: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
  targetAssayRuns: [
    {
      id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
    },
  ],
};

export const SCHEMA_FIELD_ASSAY_REQUEST_LINK: AssayLinkSchemaField = {
  apiId: 'tsf_as4nk34',
  archiveRecord: null,
  dbId: 85,
  displayName: 'flow_request',
  fieldType: FieldType.ASSAY_REQUEST_LINK,
  id: 85,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  isRequired: false,
  name: 'flow_request',
  numericMax: null,
  numericMin: null,
  requiredLink: {
    assaySchema: ASSAY_SCHEMAS_BY_ID.assaysch_1,
  },
  schemaFieldSelectorId: null,
  systemName: 'flow_request',
};

export const SCHEMA_TAG_ASSAY_REQUEST_LINK = {
  tagSchemaField: SCHEMA_FIELD_ASSAY_REQUEST_LINK,
  computedState: null,
  name: 'flow_request',
  value: 'asdf',
  targetAssayRequests: [
    {
      id: 'asdf',
    },
  ],
};

export const SCHEMA_FIELD_ASSAY_RESULT_LINK: AssayLinkSchemaField = {
  apiId: 'tsf_as4nk34',
  archiveRecord: null,
  dbId: 86,
  displayName: 'flow_result',
  fieldType: FieldType.ASSAY_RESULT_LINK,
  id: 86,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isParentLink: false,
  isMulti: false,
  isRequired: false,
  isIntegration: false,
  name: 'flow_result',
  numericMax: null,
  numericMin: null,
  requiredLink: {
    assaySchema: ASSAY_SCHEMAS_BY_ID.assaysch_4,
  },
  schemaFieldSelectorId: null,
  systemName: 'flow_result',
};

export const SCHEMA_FIELD_JSON: SchemaField = {
  id: 88,
  apiId: 'tsf_Jn6wt5l',
  dbId: 88,
  fieldType: FieldType.JSON,
  name: 'json',
  displayName: 'JSON',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  systemName: 'json',
  archiveRecord: null,
};

export const SCHEMA_TAG_JSON: SchemaTag = {
  tagSchemaField: SCHEMA_FIELD_JSON,
  computedState: null,
  name: 'json',
  value: '{asdf: 5}',
  jsonValue: {
    asdf: 5,
  },
};

export const SCHEMA_FIELD_BOOLEAN: SchemaField = {
  id: 84,
  apiId: 'tsf_DtWaRL64',
  dbId: 84,
  fieldType: FieldType.BOOLEAN,
  name: 'boolean',
  displayName: 'Boolean',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  schemaFieldSelectorId: null,
  systemName: 'boolean',
  archiveRecord: null,
};

export const SCHEMA_TAG_BOOLEAN: SchemaTag = {
  name: 'boolean',
  tagSchemaField: SCHEMA_FIELD_BOOLEAN,
  booleanValue: true,
  value: 'true',
};

export const CONTAINABLE_TAG_SCHEMA = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_CONTANBL',
  name: 'Containable Strain',
  isContainable: true,
  containableType: TagSchemaContainableType.ENTITY,
};

export const CONTAINABLE_TAG_SCHEMA_GQL = restToGraphQL('TagSchema', CONTAINABLE_TAG_SCHEMA);

export const CONTAINABLE_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: CONTAINABLE_TAG_SCHEMA.id,
  name: CONTAINABLE_TAG_SCHEMA.name,
  isContainable: true,
  containableType: TagSchemaContainableType.ENTITY,
};

export const CONTAINABLE_TAG_SCHEMA_2 = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_COOLCONT',
  name: 'My Cool Containable',
  isContainable: true,
  containableType: TagSchemaContainableType.ENTITY,
};

export const CONTAINABLE_TAG_SCHEMA_2_GQL = restToGraphQL('TagSchema', CONTAINABLE_TAG_SCHEMA_2);

export const CONTAINABLE_TAG_SCHEMA_2_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: CONTAINABLE_TAG_SCHEMA_2.id,
  name: CONTAINABLE_TAG_SCHEMA_2.name,
  isContainable: true,
  containableType: TagSchemaContainableType.ENTITY,
};

export const FIELD_FOR_TAG_ERROR: SchemaField = {
  ...SCHEMA_FIELD_TEXT,
  name: 'Accuracy',
  isRequired: true,
  displayName: 'Accuracy',
};

export const SCHEMA_TAG_ERROR_REQUIRED_FIELD: SchemaValidationErrorRecord = {
  errorLabel: REQUIRED_FIELD_ERROR_LABEL,
  message: 'Value is missing for required field "Accuracy"',
  field: FIELD_FOR_TAG_ERROR,
};

export const SCHEMA_TAG_ERROR_OTHER_FIELD: SchemaValidationErrorRecord = {
  errorLabel: 'Something random',
  message: 'Ya did it wrong',
  field: FIELD_FOR_TAG_ERROR,
};

export const TAG_SCHEMA_VALIDATION_STATUS_SUMMARY: ValidationStatusSummary = {
  numPassed: 10,
  numFailed: 5,
  numOverridden: 3,
  numValidating: 1,
};

export const TAG_SCHEMA_VALIDATION_STATUS_SUMMARY_GQL: ValidationStatusSummaryGraphQL = {
  __typename: 'TagSchemaValidationStatusSummaryNode',
  ...TAG_SCHEMA_VALIDATION_STATUS_SUMMARY,
};

const TAG_SCHEMA_ID = 'ts_ab111';

export const TAG_SCHEMA_WITH_VALIDATION_STATUS_SUMMARY: ItemWithValidationStatusSummary = {
  id: TAG_SCHEMA_ID,
  validationStatusSummary: TAG_SCHEMA_VALIDATION_STATUS_SUMMARY,
};

export const TAG_SCHEMA_WITH_VALIDATION_STATUS_SUMMARY_GQL: TagSchemaValidationStatusSummaryGraphQL = {
  __typename: 'TagSchemaNode',
  id: TAG_SCHEMA_ID,
  validationStatusSummary: TAG_SCHEMA_VALIDATION_STATUS_SUMMARY_GQL,
};

export const TAG_SCHEMA_VALIDATION_ERROR_LABEL_SUMMARY: ValidationErrorLabelSummary = {
  numConfigurableValidationRuleFailure: 1,
  numConstraintViolation: 1,
  numInvalidLink: 2,
  numInvalidMultipleLink: 3,
  numInvalidValue: 4,
  numMissingRequired: 5,
  numOther: 6,
};

export const TAG_SCHEMA_VALIDATION_ERROR_LABEL_SUMMARY_GQL: ValidationErrorLabelSummaryGraphQL = {
  __typename: 'TagSchemaValidationErrorLabelSummaryNode',
  ...TAG_SCHEMA_VALIDATION_ERROR_LABEL_SUMMARY,
};

export const TAG_SCHEMA_WITH_VALIDATION_ERROR_LABEL_SUMMARY: ItemWithValidationErrorLabelSummary = {
  id: TAG_SCHEMA_ID,
  validationErrorLabelSummary: TAG_SCHEMA_VALIDATION_ERROR_LABEL_SUMMARY,
};

export const TAG_SCHEMA_WITH_VALIDATION_ERROR_LABEL_SUMMARY_GQL: TagSchemaValidationErrorLabelSummaryGraphQL =
  {
    __typename: 'TagSchemaNode',
    id: TAG_SCHEMA_ID,
    validationErrorLabelSummary: TAG_SCHEMA_VALIDATION_ERROR_LABEL_SUMMARY_GQL,
  };

export const BFI_TAG_SCHEMA_WITH_FIELD: TagSchema = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_lskdjlot',
  name: 'Strain 3',
  fields: [SCHEMA_FIELD_INTEGER],
};

export const BFI_TAG_SCHEMA_LISTING_WITH_ONE_FIELD_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: BFI_TAG_SCHEMA_WITH_FIELD.id,
  name: BFI_TAG_SCHEMA_WITH_FIELD.name,
  allFields: BFI_TAG_SCHEMA_WITH_FIELD.allFields,
  fields: BFI_TAG_SCHEMA_WITH_FIELD.fields,
};

export const BFI_SOURCE_TAG_SCHEMA: TagSchema = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_orgdjlot',
  name: 'Original tag schema',
  fields: [SCHEMA_FIELD_INTEGER, SCHEMA_FIELD_TEXT],
};

export const BFI_SOURCE_TAG_SCHEMA_GQL: TagSchemaGraphQL = {
  ...BFI_TAG_SCHEMA_GQL,
  id: BFI_SOURCE_TAG_SCHEMA.id,
  name: BFI_SOURCE_TAG_SCHEMA.name,
  fieldsJson: [SCHEMA_FIELD_INTEGER, SCHEMA_FIELD_TEXT],
};

export const BFI_SOURCE_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: BFI_SOURCE_TAG_SCHEMA.id,
  name: BFI_SOURCE_TAG_SCHEMA.name,
  allFields: BFI_SOURCE_TAG_SCHEMA.allFields,
  fields: BFI_SOURCE_TAG_SCHEMA.fields,
};

export const BFI_TARGET_TAG_SCHEMA: TagSchema = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_tgtdjlot',
  name: 'Target tag schema',
  fields: [SCHEMA_FIELD_INTEGER_2, SCHEMA_FIELD_TEXT_2],
};

export const BFI_TARGET_TAG_SCHEMA_GQL: TagSchemaGraphQL = {
  ...BFI_TAG_SCHEMA_GQL,
  id: BFI_TARGET_TAG_SCHEMA.id,
  name: BFI_TARGET_TAG_SCHEMA.name,
  fieldsJson: [SCHEMA_FIELD_INTEGER_2, SCHEMA_FIELD_TEXT_2],
};

export const BFI_TARGET_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: BFI_TARGET_TAG_SCHEMA.id,
  name: BFI_TARGET_TAG_SCHEMA.name,
  allFields: BFI_TARGET_TAG_SCHEMA.allFields,
  fields: BFI_TARGET_TAG_SCHEMA.fields,
};

export const SEQUENCE_DEPENDENT_TAG_SCHEMA: TagSchema = {
  ...TAG_SCHEMA,
  id: 'ts_seqdepen',
  name: 'tag schema w/ fields dependent on TAG_SCHEMA',
  fields: [
    {
      ...SCHEMA_FIELD_BIOENTITY_LINK,
      requiredLink: {
        folderItemType: null,
        entitySchemaFieldPath: null,
        tagSchema: TAG_SCHEMA_SUMMARY,
        schemaInterface: null,
      },
    },
    {
      ...SCHEMA_FIELD_PART_LINK,
      requiredLink: {
        folderItemType: null,
        entitySchemaFieldPath: null,
        tagSchema: TAG_SCHEMA_SUMMARY,
        schemaInterface: null,
      },
    },
    {...SCHEMA_FIELD_COMPUTED},
  ],
};

export const SEQUENCE_DEPENDENT_TAG_SCHEMA_GQL: TagSchemaGraphQL = {
  ...TAG_SCHEMA_GQL,
  id: SEQUENCE_DEPENDENT_TAG_SCHEMA.id,
  name: SEQUENCE_DEPENDENT_TAG_SCHEMA.name,
  fieldsJson: SEQUENCE_DEPENDENT_TAG_SCHEMA.fields,
};

export const SEQUENCE_WITH_SEQUENCE_SPECIFIC_LINK: TagSchema = {
  ...TAG_SCHEMA,
  id: 'ts_seqanyent',
  name: 'Tag schema with a custom entity link',
  fields: [
    {
      ...SCHEMA_FIELD_BIOENTITY_LINK,
      requiredLink: {
        folderItemType: 'sequence',
        entitySchemaFieldPath: null,
        tagSchema: null,
        schemaInterface: null,
      },
    },
  ],
};

export const SEQUENCE_WITH_SEQUENCE_SPECIFIC_LINK_GQL: TagSchemaGraphQL = {
  ...TAG_SCHEMA_GQL,
  id: SEQUENCE_WITH_SEQUENCE_SPECIFIC_LINK.id,
  name: SEQUENCE_WITH_SEQUENCE_SPECIFIC_LINK.name,
  fieldsJson: SEQUENCE_WITH_SEQUENCE_SPECIFIC_LINK.fields,
};

export const PROTEIN_TAG_SCHEMA_WITH_FIELDS: TagSchema = {
  ...PROTEIN_TAG_SCHEMA,
  fields: [SCHEMA_FIELD_DATE, SCHEMA_FIELD_BOOLEAN],
  allFields: [SCHEMA_FIELD_DATE, SCHEMA_FIELD_BOOLEAN],
};

export const PROTEIN_TAG_SCHEMA_WITH_FIELDS_GQL: TagSchemaGraphQL = {
  ...PROTEIN_TAG_SCHEMA_GQL,
  fieldsJson: [SCHEMA_FIELD_DATE, SCHEMA_FIELD_BOOLEAN],
  allFieldsJson: [SCHEMA_FIELD_DATE, SCHEMA_FIELD_BOOLEAN],
};

export const SEQUENCE_TAG_SCHEMA_WITH_FIELDS: TagSchema = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_seq123',
  name: 'Sequence',
  folderItemType: ModelTypeConstants.SEQUENCE,
  fields: [SCHEMA_FIELD_FLOAT_2, SCHEMA_FIELD_INTEGER_2],
  allFields: [SCHEMA_FIELD_FLOAT_2, SCHEMA_FIELD_INTEGER_2],
};

export const SEQUENCE_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: SEQUENCE_TAG_SCHEMA_WITH_FIELDS.id,
  name: SEQUENCE_TAG_SCHEMA_WITH_FIELDS.name,
  allFields: SEQUENCE_TAG_SCHEMA_WITH_FIELDS.allFields,
  fields: SEQUENCE_TAG_SCHEMA_WITH_FIELDS.fields,
};

export const SEQUENCE_TAG_SCHEMA_WITH_ARCHIVED_FIELDS: TagSchema = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_seq123',
  name: 'Sequence',
  folderItemType: ModelTypeConstants.SEQUENCE,
  fields: [SCHEMA_FIELD_FLOAT_2, SCHEMA_FIELD_INTEGER_2],
  allFields: [SCHEMA_FIELD_FLOAT_2, SCHEMA_FIELD_INTEGER_2, SCHEMA_FIELD_FLOAT_ARCHIVED],
};

export const BFI_TAG_SCHEMA_REQUIRED_FIELD = {
  ...BFI_TAG_SCHEMA,
  id: 'ts_req',
  name: 'BFI Required Field',
  allFields: [FIELD_FOR_TAG_ERROR, SCHEMA_FIELD_TEXT, SCHEMA_FIELD_INTEGER],
  fields: [FIELD_FOR_TAG_ERROR, SCHEMA_FIELD_TEXT, SCHEMA_FIELD_INTEGER],
};

export const BFI_TAG_SCHEMA_REQUIRED_FIELD_GQL = restToGraphQL('TagSchema', BFI_TAG_SCHEMA_REQUIRED_FIELD);

export const BFI_TAG_SCHEMA_REQUIRED_FIELD_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...BFI_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  id: BFI_TAG_SCHEMA_REQUIRED_FIELD.id,
  name: BFI_TAG_SCHEMA_REQUIRED_FIELD.name,
  allFields: BFI_TAG_SCHEMA_REQUIRED_FIELD.allFields,
  fields: BFI_TAG_SCHEMA_REQUIRED_FIELD.fields,
};

export const BFI_TAG_SCHEMA_REQUIRED_FIELD_WITH_ITEM_TYPE_GQL = {
  ...BFI_TAG_SCHEMA_REQUIRED_FIELD_GQL,
  itemType: TagSchemaItemType.BASIC_FOLDER_ITEM,
};

export const MOLECULE_TAG_SCHEMA_WITH_FIELDS: TagSchema = {
  ...MOLECULE_TAG_SCHEMA,
  fields: [SCHEMA_FIELD_TEXT_2, SCHEMA_FIELD_BIOENTITY_LINK],
  allFields: [SCHEMA_FIELD_TEXT_2, SCHEMA_FIELD_BIOENTITY_LINK],
};

export const MOLECULE_TAG_SCHEMA_WITH_SMILES_FIELD: TagSchema = {
  ...MOLECULE_TAG_SCHEMA,
  fields: [
    {...SCHEMA_FIELD_TEXT_2, name: 'SMILES', displayName: 'SMILES', systemName: 'smiles'},
    SCHEMA_FIELD_BIOENTITY_LINK,
  ],
  allFields: [
    {...SCHEMA_FIELD_TEXT_2, name: 'SMILES', displayName: 'SMILES', systemName: 'smiles'},
    SCHEMA_FIELD_BIOENTITY_LINK,
  ],
};

export const MOLECULE_TAG_SCHEMA_WITH_SMILES_FIELD_GQL: TagSchemaGraphQL = {
  ...MOLECULE_TAG_SCHEMA_GQL,
  fieldsJson: [
    {...SCHEMA_FIELD_TEXT_2, name: 'SMILES', displayName: 'SMILES', systemName: 'smiles'},
    SCHEMA_FIELD_BIOENTITY_LINK,
  ],
  allFieldsJson: [
    {...SCHEMA_FIELD_TEXT_2, name: 'SMILES', displayName: 'SMILES', systemName: 'smiles'},
    SCHEMA_FIELD_BIOENTITY_LINK,
  ],
};

export const MIXTURE_RECIPE_TAG_SCHEMA_WITH_FIELDS: TagSchema = {
  ...MIXTURE_RECIPE_TAG_SCHEMA,
  fields: [SCHEMA_FIELD_BIOENTITY_LINK_2],
  allFields: [SCHEMA_FIELD_BIOENTITY_LINK_2],
};

export const MIXTURE_RECIPE_TAG_SCHEMA_LISTING_WITH_FIELDS_GQL: TagSchemaListingWithFieldsGraphQL = {
  ...TAG_SCHEMA_LISTING_WITH_FIELDS_GQL,
  ...MIXTURE_RECIPE_TAG_SCHEMA_LISTING_GQL,
  fields: MIXTURE_RECIPE_TAG_SCHEMA_WITH_FIELDS.fields,
  allFields: MIXTURE_RECIPE_TAG_SCHEMA_WITH_FIELDS.allFields,
};

export const FieldTypeKeyArbitrary: Array<FastCheck.Arbitrary<keyof typeof FieldType>> = Object.keys(
  FieldType
).map((key) => FastCheck.constant(key as keyof typeof FieldType));

export const FieldTypeArbitrary: FastCheck.Arbitrary<FieldType> = FastCheck.oneof(
  ...FieldTypeKeyArbitrary.map((fieldTypeKeyArbitrary) => fieldTypeKeyArbitrary.map((key) => FieldType[key]))
);

export const ArchiveRecordArbitrary: FastCheck.Arbitrary<ArchiveRecord> = FastCheck.string().map(
  (purpose) => ({purpose})
);

export function schemaFieldOfFieldTypes(
  filteredFieldTypes: Array<FieldType>
): FastCheck.Arbitrary<SchemaField> {
  return SchemaFieldArbitrary.filter(({fieldType}) => filteredFieldTypes.includes(fieldType));
}

export function schemaFieldNotOfFieldTypes(
  filteredFieldTypes: Array<FieldType>
): FastCheck.Arbitrary<SchemaField> {
  return SchemaFieldArbitrary.filter(({fieldType}) => !filteredFieldTypes.includes(fieldType));
}

export function schemaFieldFromFieldType(fieldType: FieldType): FastCheck.Arbitrary<SchemaField> {
  return FastCheck.tuple(
    FastCheck.integer(),
    FastCheck.integer(),
    apiIdentifierArbitrary('tsf'),
    FastCheck.string(),
    FastCheck.string(),
    FastCheck.string(),
    FastCheck.boolean(),
    FastCheck.boolean(),
    FastCheck.boolean(),
    FastCheck.boolean(),
    FastCheck.boolean(),
    FastCheck.boolean(),
    FastCheck.boolean(),
    FastCheck.constant(null),
    FastCheck.option(ArchiveRecordArbitrary)
  ).map(
    ([
      id,
      dbId,
      apiId,
      name,
      systemName,
      displayName,
      isRequired,
      isMulti,
      isComputed,
      isConvertedFromLink,
      isSnapshot,
      isIntegration,
      isParentLink,
      requiredLink,
      archiveRecord,
    ]) => ({
      id,
      dbId,
      apiId,
      name,
      systemName,
      displayName,
      fieldType,
      isRequired,
      isMulti,
      isComputed,
      isConvertedFromLink,
      isSnapshot,
      isIntegration,
      isParentLink,
      requiredLink,
      archiveRecord,
    })
  );
}

export const SchemaFieldArbitrary: FastCheck.Arbitrary<SchemaField> = FastCheck.tuple(
  FastCheck.integer(),
  FastCheck.integer(),
  apiIdentifierArbitrary('tsf'),
  FastCheck.string(),
  FastCheck.string(),
  FastCheck.string(),
  FieldTypeArbitrary,
  FastCheck.boolean(),
  FastCheck.boolean(),
  FastCheck.boolean(),
  FastCheck.boolean(),
  FastCheck.boolean(),
  FastCheck.boolean(),
  FastCheck.boolean(),
  FastCheck.constant(null),
  FastCheck.option(ArchiveRecordArbitrary)
).map(
  ([
    id,
    dbId,
    apiId,
    name,
    systemName,
    displayName,
    fieldType,
    isRequired,
    isMulti,
    isComputed,
    isConvertedFromLink,
    isSnapshot,
    isIntegration,
    isParentLink,
    requiredLink,
    archiveRecord,
  ]) => {
    const schemaField: SchemaField = {
      id,
      dbId,
      apiId,
      name,
      systemName,
      displayName,
      fieldType,
      isRequired,
      isMulti,
      isComputed,
      isConvertedFromLink,
      isSnapshot,
      isIntegration,
      isParentLink,
      requiredLink,
      archiveRecord,
    };
    if (isSnapshot) {
      schemaField.lookupConfig = {
        isMulti: false,
        multiFlattenLevel: null,
        // The lookup step to referencing the snapshot field isn't allowed, but should be fine for tests
        lookupSteps: [{schemaField: systemName, type: LookupStepType.SCHEMA_FIELD}],
      };
    }
    return schemaField;
  }
);

export const NotArchivedSchemaFieldArbitrary: FastCheck.Arbitrary<SchemaField> = SchemaFieldArbitrary.filter(
  (field) => !isSchemaFieldArchived(field)
);

export const SCHEMA_FIELD_UNEDITABLE: SchemaField = {
  ...SCHEMA_FIELD_TEXT,
  isUneditable: true,
};

export const SCHEMA_TAG_UNEDITABLE: SchemaTag = {
  name: 'Cat',
  tagSchemaField: SCHEMA_FIELD_UNEDITABLE,
  url: '',
  value: 'Nacho',
};

export const SCHEMA_FIELD_EQUIPMENT_MODEL: SchemaField = {
  ...SCHEMA_FIELD_BIOENTITY_LINK,
  systemName: MODEL_FIELD_SYSTEM_NAME,
  displayName: MODEL_FIELD_DISPLAY_NAME,
  isRequired: true,
  name: MODEL_FIELD_DISPLAY_NAME,
};

export const EQUIPMENT_TAG_SCHEMA: TagSchema = {
  ...TAG_SCHEMA,
  allFields: [SCHEMA_FIELD_EQUIPMENT_MODEL],
};

export const EQUIPMENT_MODEL_SCHEMA_TAG: SchemaTag = {
  ...SCHEMA_TAG_BIOENTITY_LINK,
  name: MODEL_FIELD_DISPLAY_NAME,
};

export const EXPIRATION_DATE_SYSTEM_FIELD: SchemaField = {
  ...SCHEMA_FIELD_DATE,
  name: EXPIRATION_DATE_FIELD_DISPLAY_NAME,
  displayName: EXPIRATION_DATE_FIELD_DISPLAY_NAME,
};

export const EXPIRATION_DATE_SCHEMA_TAG: SchemaTag = {
  ...SCHEMA_TAG_DATE,
  tagSchemaField: EXPIRATION_DATE_SYSTEM_FIELD,
  name: EXPIRATION_DATE_FIELD_DISPLAY_NAME,
};
